import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import api, { setRoleManagerStatus } from '../utils/api';
import EventPopup from './EventPopup';
import EventPreview from './EventPreview';
import { expandAllRecurringEvents, isRecurringInstance, getOriginalEventId } from '../utils/recurrence';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';

const getStyles = (viewMode) => ({
    container: {
        backgroundColor: 'var(--bg-primary)',
        borderRadius: '8px',
        boxShadow: 'none',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 10,
        marginTop: '-60px', // Increase negative margin to reduce gap
        display: 'flex',
        flexDirection: 'column',
    },
    mainContent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        marginTop: '0',
        zIndex: 10,
    },
    topScrollContainer: {
        height: '12px',
        overflowX: 'auto',
        overflowY: 'hidden',
        marginBottom: '0',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '6px',
        zIndex: 3,
        className: 'topScrollContainer',
        marginLeft: '300px',
        width: 'calc(100% - 300px)',
        position: 'relative',
        WebkitScrollbarHeight: '12px',
        WebkitScrollbarBackground: 'var(--bg-secondary)',
        WebkitScrollbarThumbBackground: 'var(--border-color)',
        WebkitScrollbarThumbBorderRadius: '6px',
        WebkitScrollbarThumbBorder: '3px solid var(--bg-secondary)',
        WebkitScrollbarTrackBackground: 'var(--bg-secondary)',
        WebkitScrollbarTrackBorderRadius: '6px',
    },
    scrollContent: {
        width: '1700px',
        height: '1px',
    },
    timeHeaderWrapper: {
        backgroundColor: 'var(--bg-primary)',
        zIndex: 2,
        overflow: 'hidden',
        width: '100%',
        display: 'flex',
        height: '70px',
        position: 'relative',
        borderTop: 'none',
        marginTop: '0', // Ensure no top margin
        paddingTop: '0', // Ensure no top padding
    },
    teamColumnSpace: {
        width: '280px',
        flexShrink: 0,
        backgroundColor: 'var(--bg-primary)',
        borderRight: '1px solid var(--border-color)',
        height: '70px',
        position: 'relative',
        zIndex: 3,
        overflow: 'hidden',
        marginTop: '-16px', // Increase negative margin to further reduce the gap
    },
    timeHeader: {
        display: 'flex',
        height: '70px',
        backgroundColor: 'var(--bg-primary)',
        width: viewMode === 'week' ? '100%' : '2550px',
        position: 'relative',
        transform: 'translateX(0)',
        transition: 'transform 0ms linear',
        flex: 1,
        overflow: 'hidden',
    },
    timeHeaderCell: {
        width: viewMode === 'week' ? 'calc(100% / 7)' : '150px',
        flexShrink: 0,
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: viewMode === 'week' ? 'flex-start' : 'center',
        justifyContent: viewMode === 'week' ? 'flex-start' : 'center',
        backgroundColor: 'var(--bg-primary)',
        height: viewMode === 'week' ? '70px' : '70px',
        transition: 'all 0.2s ease',
        padding: '4px',
        paddingTop: '8px',
    },
    timeContentWrapper: {
        width: viewMode === 'week' ? '100%' : '70px',
        height: viewMode === 'week' ? '100%' : '70px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: viewMode === 'week' ? 'flex-start' : 'center',
        justifyContent: viewMode === 'week' ? 'flex-start' : 'center',
        borderRadius: '6px',
        padding: viewMode === 'week' ? '8px 12px' : '0px 12px',
    },
    timeNumber: {
        fontSize: '32px',
        fontWeight: '800',
        lineHeight: '1',
        marginBottom: '6px',
        textAlign: viewMode === 'week' ? 'left' : 'center',
        display: 'block',
        color: '#C0BFBF',
        fontFamily: 'Inter, sans-serif',
    },
    timePeriod: {
        fontSize: '14px',
        color: 'var(--text-secondary)',
        lineHeight: '1',
        textAlign: 'left',
        display: 'block',
    },
    fullWidthHeader: {
        height: '32px',
        backgroundColor: '#f5f6f8',
        borderBottom: '1px solid #e5e5e5',
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        fontWeight: '500',
        fontSize: '16px',
        color: '#333',
        width: '100%',
        position: 'relative',
        zIndex: 2,
    },
    scheduleContent: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0, // Important for flex container
    },
    teamColumn: {
        width: '280px',
        flexShrink: 0,
        backgroundColor: 'var(--bg-primary)',
        borderRight: '1px solid var(--border-color)',
        zIndex: 3,
        position: 'sticky',
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
    },
    settingsContainer: {
        position: 'relative',
    },
    settingsButton: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e5e5e5',
        borderRadius: '6px',
        cursor: 'pointer',
        color: '#333',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
    },
    employeeCell: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        borderBottom: '1px solid #e0e0e0', // Fallback color
        borderBottomColor: 'var(--border-color)', // CSS variable for theme support
        gap: '12px',
        backgroundColor: 'var(--bg-primary)',
        minHeight: '50px', // Keep the reduced height to ensure 5 employees fit
        height: 'calc(var(--available-height, calc(100vh - 160px)) / var(--visible-members))',
        transition: 'all 0.2s ease',
        minWidth: '0',
        position: 'relative',
    },
    avatar: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '500',
        fontSize: '18px',
        transition: 'all 0.2s ease'
    },
    employeeInfo: {
        flex: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        minWidth: '0',
        gap: '8px',
    },
    employeeDetails: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '0',
        flex: 1,
    },
    employeeName: {
        fontSize: '14px',
        fontWeight: '500',
        color: 'var(--text-primary)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    employeeRole: {
        fontSize: '12px',
        color: 'var(--text-secondary)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    hoursInfo: {
        fontSize: '14px',
        color: '#4263eb',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    timeRow: {
        display: 'flex',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        height: 'calc(var(--available-height, calc(100vh - 160px)) / var(--visible-members))',
        minHeight: '50px', // Reduced from 60px to match employeeCell
        position: 'relative',
    },
    timeSlot: {
        width: viewMode === 'week' ? 'calc(100% / 7)' : '150px',
        flexShrink: 0,
        borderRight: '1px solid var(--border-color)',
        borderBottom: '1px solid var(--border-color)',
        position: 'relative',
        backgroundColor: 'var(--bg-primary)',
        height: '100%',
        padding: viewMode === 'week' ? '8px' : '4px',
        display: viewMode === 'week' ? 'flex' : 'block',
        flexDirection: 'column',
        gap: '8px',
    },
    event: {
        position: viewMode === 'week' ? 'relative' : 'absolute',
        top: viewMode === 'week' ? 'auto' : '36px',
        left: viewMode === 'week' ? 'auto' : '8px',
        height: viewMode === 'week' ? '80%' : '68px',
        borderRadius: '8px',
        padding: viewMode === 'week' ? '8px 12px' : '6px 8px',
        fontSize: viewMode === 'week' ? '14px' : '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: viewMode === 'week' ? 'auto' : 'calc(100% - 16px)',
        boxShadow: viewMode === 'week' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
        margin: viewMode === 'week' ? '4px 0' : '0',
        zIndex: 1,
    },
    summaryBarContainer: {
        display: 'flex',
        width: '100%',
        backgroundColor: 'var(--bg-primary)',
        borderBottom: 'none',
    },
    summaryBarSpace: {
        width: '300px',
        flexShrink: 0,
        backgroundColor: 'var(--bg-primary)',
    },
    summaryBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '16px 24px',
        backgroundColor: 'var(--bg-primary)',
        flex: 1,
        gap: '4px',
        borderBottom: 'none',
    },
    summaryItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    summaryLabel: {
        fontSize: '12px',
        color: 'var(--text-secondary)',
        marginBottom: '4px',
    },
    summaryValue: {
        fontSize: '24px',
        fontWeight: '600',
        color: 'var(--text-primary)',
    },
    summaryValueWithProgress: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    summaryProgress: {
        width: '100%',
        height: '16px',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '4px',
        overflow: 'hidden',
    },
    progressBar: {
        height: '100%',
        backgroundColor: '#4263eb',
        borderRadius: '2px',
        transition: 'width 0.3s ease',
    },
    weekDayName: {
        fontSize: '14px',
        fontWeight: '500',
    },
    weekDayDate: {
        fontSize: '36px',
        fontWeight: '600',
        lineHeight: '0.9',
    },
    dayColumn: {
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--border-color)',
    },
    settingsPanel: {
        position: 'absolute',
        bottom: '100%',
        left: '12px',
        right: '12px',
        backgroundColor: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        overflow: 'hidden',
    },
    settingsItem: {
        padding: '12px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        color: 'var(--text-primary)',
    },
    monthText: {
        fontSize: '32px',
        fontWeight: '600',
        color: 'var(--text-primary)',
    },
    yearText: {
        fontSize: '32px',
        color: 'var(--text-primary)',
        fontWeight: '600',
        marginLeft: '8px',
    },
    scheduleWrapper: {
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
    },
    scheduleSection: {
        width: '100%',
        height: '100%',
        overflow: 'auto',
        position: 'relative',
    },
    scheduleGrid: {
        width: viewMode === 'week' ? '100%' : '2550px',
        height: '100%',
        position: 'relative',
        transform: 'translateX(0)',
        transition: 'transform 0ms linear',
    },
    expandButton: {
        width: '20px',
        height: '20px',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'absolute',
        top: '8px',
        right: '8px',
        color: 'rgba(255, 255, 255, 0.4)',
        padding: '0',
        flexShrink: 0,
    },
    expandedInfo: {
        position: 'absolute',
        left: '12px',
        right: '12px',
        top: '100%',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '8px',
        padding: '12px',
        border: '1px solid var(--border-color)',
        zIndex: 10,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        marginTop: '4px',
    },
    expandedInfoRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
        fontSize: '13px',
        color: 'var(--text-primary)',
    },
    expandedInfoLabel: {
        color: 'var(--text-secondary)',
        marginRight: '12px',
    },
    expandedInfoValue: {
        color: 'var(--text-primary)',
        fontWeight: '500',
    },
});

const CustomCalendar = ({ employees, schedules, setSchedules, onScheduleCreate, viewMode, refreshSchedules, currentDate }) => {
    const styles = getStyles(viewMode);
    const hours = Array.from({ length: 17 }, (_, i) => i + 7); // 7 AM to 11 PM
    const calendarDate = currentDate || moment(); // Use passed currentDate or fallback to current date
    const currentHour = calendarDate.hour();

    // State declarations - moved here to be available to all functions
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [isEventPopupOpen, setIsEventPopupOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [dragEnd, setDragEnd] = useState(null);
    const [dragEmployee, setDragEmployee] = useState(null);
    const [dragDate, setDragDate] = useState(null);
    const [highlightedSlots, setHighlightedSlots] = useState(new Set());
    const [justFinishedDrag, setJustFinishedDrag] = useState(false);
    const [actuallyDragged, setActuallyDragged] = useState(false);
    const [existingEvent, setExistingEvent] = useState(null);
    const [prefilledParticipants, setPrefilledParticipants] = useState([]);
    const [prefilledEmployeeId, setPrefilledEmployeeId] = useState(null);
    const [expandedEmployees, setExpandedEmployees] = useState(new Set());
    const [editingRole, setEditingRole] = useState(null);
    const [newRole, setNewRole] = useState('');
    const [selectedColor, setSelectedColor] = useState('#4CAF50');
    const [teamMemberLimit, setTeamMemberLimit] = useState(() => {
        // Responsive default based on screen height:
        // ≤800px height: 3 people per page
        // ≤1000px height: 4 people per page  
        // >1000px height: 5 people per page
        const height = window.innerHeight;
        if (height <= 800) return 3;
        if (height <= 1000) return 4;
        return 5;
    });
    const [currentPage, setCurrentPage] = useState(1); // Add pagination state
    const [windowDimensions, setWindowDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const [expandedDays, setExpandedDays] = useState(new Set());
    const MAX_VISIBLE_EVENTS = 2; // Maximum events to show before "show more" button
    
    // EventPreview state
    const [eventPreview, setEventPreview] = useState(null);
    const [eventPreviewPosition, setEventPreviewPosition] = useState({ x: 0, y: 0 });
    const [selectedHour, setSelectedHour] = useState(null);

    // Event dragging state
    const [draggingEvent, setDraggingEvent] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dragPreview, setDragPreview] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);

    // Add ref to measure actual cell height
    const cellRef = useRef(null);
    const [actualCellHeight, setActualCellHeight] = useState(80);

    // Ref to store dragging event for immediate access
    const draggingEventRef = useRef(null);
    const dropTargetRef = useRef(null);

    // Add hover state for time slots
    const [hoveredSlot, setHoveredSlot] = useState(null);

    // Progress bar settings with localStorage persistence
    const [progressBarSettings, setProgressBarSettings] = useState(() => {
        const saved = localStorage.getItem('progressBarSettings');
        return saved ? JSON.parse(saved) : {
            showWeekly: true,
            showMonthly: true,
            showDaily: false,
            showTTM: false,
            showYearly: false
        };
    });
    
    const [showProgressSettings, setShowProgressSettings] = useState(false);

    // Function to get employee role (check localStorage first, then fallback to original)
    const getEmployeeRole = useCallback((employee) => {
        const savedRoles = JSON.parse(localStorage.getItem('employeeRoles') || '{}');
        return savedRoles[employee.id] || employee.role || 'Employee';
    }, []);

    // Process employees with grouping and pagination
    const groupedEmployees = useMemo(() => {
        console.log('🎯 Processing employees with pagination:', {
            totalEmployees: employees.length,
            teamMemberLimit,
            currentPage,
            employees: employees.map(emp => ({ id: emp.id, name: emp.first_name + ' ' + emp.last_name }))
        });

        const grouped = employees.reduce((acc, employee) => {
            const role = getEmployeeRole(employee);
            if (!acc[role]) {
                acc[role] = [];
            }
            acc[role].push(employee);
            return acc;
        }, {});

        const groupedArray = Object.entries(grouped).map(([role, employees]) => ({
            role,
            employees
        }));

        // Flatten all employees for pagination
        const allEmployees = groupedArray.flatMap(group => group.employees);
        
        // Calculate pagination
        const totalPages = Math.ceil(allEmployees.length / teamMemberLimit);
        const startIndex = (currentPage - 1) * teamMemberLimit;
        const endIndex = Math.min(startIndex + teamMemberLimit, allEmployees.length);
        const paginatedEmployees = allEmployees.slice(startIndex, endIndex);

        // Get unique roles from paginated employees
        const roles = [...new Set(paginatedEmployees.map(emp => getEmployeeRole(emp)))];

        // Group employees by role
        const result = roles.map(role => ({
            role,
            employees: paginatedEmployees.filter(emp => getEmployeeRole(emp) === role)
        }));

        console.log('📋 All employees (before pagination):', allEmployees.length, allEmployees.map(emp => ({ id: emp.id, name: emp.first_name + ' ' + emp.last_name })));
        
        console.log('📄 Pagination calculations:', { totalPages, startIndex, endIndex, slicedCount: paginatedEmployees.length });
        console.log('📄 Paginated employees:', paginatedEmployees.map(emp => ({ id: emp.id, name: emp.first_name + ' ' + emp.last_name })));

        console.log('📊 Final grouped result:', result);
        console.log('📊 Total employees in grouped result:', result.reduce((total, group) => total + group.employees.length, 0));
        console.log('📊 Groups breakdown:', result.map(group => ({ 
            role: group.role, 
            count: group.employees.length,
            employees: group.employees.map(emp => emp.first_name + ' ' + emp.last_name)
        })));

        return result;
    }, [employees, getEmployeeRole, teamMemberLimit, currentPage]);

    // Debug effect to understand the off-by-one issue
    React.useEffect(() => {
        const actualCount = groupedEmployees.reduce((total, group) => total + group.employees.length, 0);
        console.log('🔍 Team Member Debug:', {
            teamMemberLimit,
            currentPage,
            actualDisplayedCount: actualCount,
            totalAvailableEmployees: employees.length,
            totalPages: Math.ceil(employees.length / teamMemberLimit),
            startIndex: (currentPage - 1) * teamMemberLimit,
            endIndex: (currentPage - 1) * teamMemberLimit + teamMemberLimit
        });
    }, [teamMemberLimit, groupedEmployees, employees.length, currentPage]);

    // Add styles for role groups
    const roleGroupStyles = {
        roleHeader: {
            padding: '8px 16px',
            backgroundColor: 'transparent', // Will be set dynamically with role color
            fontSize: '12px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '24px',
        },
        roleCount: {
            fontSize: '11px',
            color: 'var(--text-secondary)',
            fontWeight: '400',
            backgroundColor: 'var(--bg-primary)',
            padding: '2px 6px',
            borderRadius: '10px',
        },
        roleSeparator: {
            height: '4px',
            backgroundColor: 'var(--bg-primary)',
        }
    };

    // Helper function to convert hex color to rgba with transparency
    const hexToRgba = useCallback((hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }, []);

    // Debug: Log schedules changes - optimized to only log when needed
    React.useEffect(() => {
        if (schedules.length > 0) {
            console.log('📊 CustomCalendar schedules updated:', {
                totalCount: schedules.length,
                schedulesByResource: schedules.reduce((acc, schedule) => {
                    const resource = schedule.resource || 'unknown';
                    acc[resource] = (acc[resource] || 0) + 1;
                    return acc;
                }, {}),
                recentSchedules: schedules.slice(-5).map(s => ({
                    id: s.id,
                    title: s.title,
                    resource: s.resource,
                    start: moment(s.start).format('YYYY-MM-DD HH:mm')
                }))
            });
        }
    }, [schedules.length]); // Only log when count changes

    // Predefined role colors
    const roleColors = [
        '#4CAF50', // Green
        '#2196F3', // Blue
        '#FF9800', // Orange
        '#9C27B0', // Purple
        '#E91E63', // Pink
        '#00BCD4', // Cyan
        '#FF5722', // Red
        '#607D8B', // Blue Gray
        '#795548', // Brown
        '#FFC107', // Amber
    ];

    // Function to get role color
    const getRoleColor = useCallback((role) => {
        // You can store role colors in localStorage or state
        const savedColors = JSON.parse(localStorage.getItem('roleColors') || '{}');
        return savedColors[role] || '#4CAF50';
    }, []);

    // Function to save role color
    const saveRoleColor = useCallback((role, color) => {
        const savedColors = JSON.parse(localStorage.getItem('roleColors') || '{}');
        savedColors[role] = color;
        localStorage.setItem('roleColors', JSON.stringify(savedColors));
    }, []);

    // Function to update employee role
    const updateEmployeeRole = async (employeeId, newRole, color) => {
        try {
            // Since the API endpoint doesn't exist, we'll store role changes locally
            // and update the employees prop if possible
            
            // Save role color
            saveRoleColor(newRole, color);
            
            // Store employee role changes in localStorage
            const savedRoles = JSON.parse(localStorage.getItem('employeeRoles') || '{}');
            savedRoles[employeeId] = newRole;
            localStorage.setItem('employeeRoles', JSON.stringify(savedRoles));
            
            console.log('Role updated locally:', { employeeId, newRole, color });
            
            // Reset editing state
            setEditingRole(null);
            setNewRole('');
            
            // Force a re-render by updating the component state
            // This will cause the grouping to update
            setExpandedEmployees(new Set(expandedEmployees));
            
            // Optionally refresh the page to see changes
            window.location.reload();
            
        } catch (error) {
            console.error('Error updating employee role:', error);
        }
    };

    // Update employee manager status
    const updateEmployeeManagerStatus = async (employeeId, isManager) => {
        try {
            const employee = employees.find(emp => emp.id === employeeId);
            if (!employee) {
                alert('Employee not found');
                return;
            }

            // Use the employee's user_id directly
            if (!employee.user_id) {
                alert('Could not find user ID for this employee. Employee data may be incomplete.');
                console.log('Employee data:', employee);
                return;
            }

            if (isManager) {
                // Set role manager status using the new API
                await setRoleManagerStatus(employee.user_id, true, employee.role);
                console.log('Role manager status granted successfully');
                alert(`${employee.first_name} ${employee.last_name} has been promoted to Manager of ${employee.role}`);
            } else {
                // Revoke role manager status
                await setRoleManagerStatus(employee.user_id, false, null);
                console.log('Role manager status revoked successfully');
                alert(`${employee.first_name} ${employee.last_name} is no longer a Manager of ${employee.role}`);
            }

            // Update local storage to persist the manager status locally
            const managerStatuses = JSON.parse(localStorage.getItem('employeeManagerStatuses') || '{}');
            if (isManager) {
                managerStatuses[employeeId] = true;
            } else {
                delete managerStatuses[employeeId];
            }
            localStorage.setItem('employeeManagerStatuses', JSON.stringify(managerStatuses));

        } catch (error) {
            console.error('Error updating role manager status:', error);
            alert('Failed to update manager status. Please try again.');
        }
    };

    // Add styles for role editing
    const roleEditStyles = {
        roleEditContainer: {
            marginTop: '12px',
            padding: '12px',
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '6px',
            border: '1px solid var(--border-color)',
        },
        roleInput: {
            width: '100%',
            padding: '8px',
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            color: 'var(--text-primary)',
            fontSize: '13px',
            marginBottom: '8px',
        },
        colorPicker: {
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap',
            marginBottom: '8px',
        },
        colorOption: {
            width: '24px',
            height: '24px',
            borderRadius: '12px',
            cursor: 'pointer',
            border: '2px solid transparent',
            transition: 'all 0.2s ease',
        },
        colorOptionSelected: {
            border: '2px solid white',
            transform: 'scale(1.1)',
        },
        roleEditButtons: {
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
        },
        roleEditButton: {
            padding: '6px 12px',
            borderRadius: '4px',
            border: 'none',
            fontSize: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        },
        saveButton: {
            backgroundColor: '#4CAF50',
            color: 'white',
        },
        cancelButton: {
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
        },
        editRoleButton: {
            padding: '4px 8px',
            backgroundColor: 'transparent',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            color: 'var(--text-secondary)',
            fontSize: '11px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginLeft: '8px',
        },
    };

    const formatHour = (hour) => {
        if (hour === 0) return '12';
        if (hour === 12) return '12';
        return hour > 12 ? `${hour - 12}` : `${hour}`;
    };

    // Helper function to get minute position within an hour slot
    const getMinuteFromPosition = (event, timeSlotElement) => {
        const rect = timeSlotElement.getBoundingClientRect();
        const relativeY = event.clientY - rect.top;
        const slotHeight = rect.height;
        const minuteRatio = relativeY / slotHeight;
        
        // If we're in the bottom 15% of the slot, consider it as the next hour
        if (minuteRatio >= 0.85) {
            console.log('Bottom of slot detected (ratio >= 0.85), setting to 60 minutes');
            return 60;
        }
        
        // Calculate minutes within this slot, snap to 15-minute intervals
        const totalMinutes = Math.round(minuteRatio * 60 / 15) * 15;
        const result = Math.max(0, Math.min(60, totalMinutes));
        
        return result;
    };

    // Helper function to create time slots with highlighting
    const getTimeSlotKey = (employeeId, hour, date) => {
        const dateStr = moment(date).format('YYYY-MM-DD');
        return `${employeeId}-${hour}-${dateStr}`;
    };

    // Handle hover on time slots to detect 30-minute segments
    const handleTimeSlotHover = (event, employee, date, hour) => {
        const timeSlotElement = event.currentTarget;
        const minute = getMinuteFromPosition(event, timeSlotElement);
        
        // Determine if we're in the first or second half
        const isFirstHalf = minute < 30;
        
        setHoveredSlot({
            employeeId: employee.id,
            hour,
            date: moment(date).format('YYYY-MM-DD'),
            isFirstHalf
        });
    };

    // Handle mouse leave from time slot
    const handleTimeSlotMouseLeave = () => {
        setHoveredSlot(null);
    };

    // Check if first half of time slot is hovered
    const isFirstHalfHovered = (employeeId, hour, date) => {
        if (!hoveredSlot) return false;
        const dateStr = moment(date).format('YYYY-MM-DD');
        return hoveredSlot.employeeId === employeeId &&
               hoveredSlot.hour === hour &&
               hoveredSlot.date === dateStr &&
               hoveredSlot.isFirstHalf;
    };

    // Check if second half of time slot is hovered
    const isSecondHalfHovered = (employeeId, hour, date) => {
        if (!hoveredSlot) return false;
        const dateStr = moment(date).format('YYYY-MM-DD');
        return hoveredSlot.employeeId === employeeId &&
               hoveredSlot.hour === hour &&
               hoveredSlot.date === dateStr &&
               !hoveredSlot.isFirstHalf;
    };

    // Check if a time slot should be highlighted
    const isSlotHighlighted = (employeeId, hour, date) => {
        if (!isDragging || !dragStart || !dragEnd || dragEmployee?.id !== employeeId) {
            return false;
        }
        
        const slotKey = getTimeSlotKey(employeeId, hour, date);
        return highlightedSlots.has(slotKey);
    };

    // Calculate highlighted slots based on drag selection
    const calculateHighlightedSlots = (startHour, startMinute, endHour, endMinute, employeeId, date) => {
        const slots = [];
        const dateStr = moment(date).format('YYYY-MM-DD');
        
        for (let hour = startHour; hour <= endHour; hour++) {
            slots.push(getTimeSlotKey(employeeId, hour, date));
        }
        
        return slots;
    };

    // Handle mouse down on time slot
    const handleTimeSlotMouseDown = (event, employee, date, hour) => {
        event.preventDefault();
        const timeSlotElement = event.currentTarget;
        const minute = getMinuteFromPosition(event, timeSlotElement);
        
        console.log('🖱️ Mouse down - drag start:', { hour, minute, employee: employee.email });
        
        setIsDragging(true);
        setActuallyDragged(false); // Reset drag flag
        setDragStart({ hour, minute });
        setDragEnd({ hour, minute }); // Start with same position, will be updated on move
        setDragEmployee(employee);
        setDragDate(date);
        
        // Start with just the starting slot highlighted
        const slots = [getTimeSlotKey(employee.id, hour, date)];
        setHighlightedSlots(new Set(slots));
        console.log('🖱️ Initial highlighted slots:', slots);
    };

    // Handle mouse move during drag
    const handleTimeSlotMouseMove = (event, employee, date, hour) => {
        if (!isDragging || !dragStart || dragEmployee?.id !== employee.id) {
            return;
        }
        
        // Prevent cross-day dragging - only allow dragging within the same date
        const dragDateStr = moment(dragDate).format('YYYY-MM-DD');
        const currentDateStr = moment(date).format('YYYY-MM-DD');
        if (dragDateStr !== currentDateStr) {
            console.log('Preventing cross-day drag:', { dragDate: dragDateStr, currentDate: currentDateStr });
            return;
        }
        
        event.preventDefault();
        const timeSlotElement = event.currentTarget;
        const minute = getMinuteFromPosition(event, timeSlotElement);
        
        console.log('🖱️ Mouse move:', { hour, minute, dragStart, employee: employee.email });
        
        // Calculate end time based on current mouse position
        let endHour = hour;
        let endMinute = minute;
        
        // If minute is 60, we're at the bottom of the slot - move to next hour
        if (minute >= 60) {
            endHour = hour + 1;
            endMinute = 0;
            console.log('🖱️ Bottom of slot detected - moving to next hour:', { endHour, endMinute });
        }
        
        // Calculate total minutes from start
        const startTotalMinutes = dragStart.hour * 60 + dragStart.minute;
        const currentTotalMinutes = endHour * 60 + endMinute;
        const durationMinutes = currentTotalMinutes - startTotalMinutes;
        
        console.log('🖱️ Duration calculation:', { 
            startTotalMinutes, 
            currentTotalMinutes, 
            durationMinutes
        });
        
        // Only apply minimum 30-minute selection if duration is 0 or negative
        if (durationMinutes <= 0) {
            // Same position or backwards - default to 30 minutes
            const minEndTotalMinutes = startTotalMinutes + 30;
            endHour = Math.floor(minEndTotalMinutes / 60);
            endMinute = minEndTotalMinutes % 60;
            console.log('🖱️ Applied minimum 30min - final:', { endHour, endMinute });
        } else {
            // Forward drag with positive duration - use actual position
            console.log('🖱️ Using actual position - duration:', durationMinutes, 'minutes');
        }
        
        setDragEnd({ hour: endHour, minute: endMinute });
        
        // Mark that we actually dragged (moved to a different position)
        if (dragStart.hour !== endHour || Math.abs(dragStart.minute - endMinute) > 15) {
            setActuallyDragged(true);
            console.log('🖱️ Actual drag movement detected');
        }
        
        // Update highlighted slots to span from start to end
        const slots = calculateHighlightedSlots(dragStart.hour, dragStart.minute, endHour, endMinute, employee.id, date);
        setHighlightedSlots(new Set(slots));
        console.log('🖱️ Updated highlighted slots:', slots, 'total slots:', slots.length);
    };

    // Handle mouse up to complete selection
    const handleTimeSlotMouseUp = (event) => {
        if (!isDragging || !dragStart || !dragEnd || !dragEmployee || !dragDate) {
            return;
        }
        
        event.preventDefault();
        
        console.log('Drag end:', { dragStart, dragEnd, highlightedSlots });
        
        // Only open EventPopup if we actually dragged (have highlighted slots or actual movement)
        if (highlightedSlots.size > 0 && actuallyDragged) {
            console.log('Using highlighted slots for time calculation');
            
            // Extract hours from highlighted slots
            const hours = Array.from(highlightedSlots).map(slot => {
                const parts = slot.split('-');
                return parseInt(parts[1]); // hour is the second part
            }).sort((a, b) => a - b);
            
            const startHour = Math.min(...hours);
            const endHour = Math.max(...hours) + 1; // Add 1 to get the end of the last hour
            
            console.log('Highlighted slot hours:', { startHour, endHour, totalHours: endHour - startHour });
            
            const finalStartTime = moment(dragDate)
                .hour(startHour)
                .minute(0)
                .second(0);
            const finalEndTime = moment(dragDate)
                .hour(endHour)
                .minute(0)
                .second(0);
                
            console.log('Final times from highlighting:', { 
                start: finalStartTime.format('HH:mm'), 
                end: finalEndTime.format('HH:mm'),
                duration: finalEndTime.diff(finalStartTime, 'minutes') + ' minutes'
            });
            
            console.log('🎯 Setting EventPopup times:', {
                selectedEmployee: dragEmployee,
                selectedStartTime: finalStartTime.toDate(),
                selectedEndTime: finalEndTime.toDate(),
                selectedDate: dragDate,
                startTimeFormatted: finalStartTime.format('YYYY-MM-DD HH:mm:ss'),
                endTimeFormatted: finalEndTime.format('YYYY-MM-DD HH:mm:ss'),
                durationMinutes: finalEndTime.diff(finalStartTime, 'minutes')
            });
            
            // Set selected times and open popup
            setSelectedEmployee(dragEmployee);
            
            // Handle week view vs other views for drag operations
            if (viewMode === 'week') {
                // Week view: always all-day events
                setSelectedHour(-1); // -1 indicates all-day event
                setSelectedStartTime('00:00');
                setSelectedEndTime('23:59');
                console.log('🎯 Drag operation in week view - setting all-day event');
            } else {
                // Other views: use calculated times
                setSelectedStartTime(finalStartTime.format('HH:mm'));
                setSelectedEndTime(finalEndTime.format('HH:mm'));
                setSelectedHour(startHour); // Use the start hour for non-week views
                console.log('🎯 Drag operation in day view - using calculated times');
            }
            
            setSelectedDate(dragDate);
            
            // Instead of opening EventPopup directly, create EventPreview like click handler
            let eventStartTime, eventEndTime;
            if (viewMode === 'week') {
                // Week view: all-day events use full day times
                const dateString = moment(dragDate).format('YYYY-MM-DD');
                eventStartTime = moment(`${dateString} 00:00`, 'YYYY-MM-DD HH:mm').toDate();
                eventEndTime = moment(`${dateString} 23:59`, 'YYYY-MM-DD HH:mm').toDate();
            } else {
                // Other views: use calculated drag times
                eventStartTime = finalStartTime.toDate();
                eventEndTime = finalEndTime.toDate();
            }
            
            const newEvent = {
                title: '',
                start: eventStartTime,
                end: eventEndTime,
                resource: dragEmployee.id,
                isNew: true,
                isAllday: viewMode === 'week', // All-day in week view, timed in other views
                employee: dragEmployee
            };
            
            // Set the event preview position based on drag end position
            setEventPreviewPosition({
                x: event.clientX,
                y: event.clientY
            });
            
            setEventPreview(newEvent);
            console.log('🎯 Created EventPreview from drag operation');
            // setIsEventPopupOpen(true); // Remove direct popup opening
        } else {
            console.log('🖱️ No actual drag movement - letting click handler show EventPreview');
        }
        
        // Reset drag state
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        setDragEmployee(null);
        setDragDate(null);
        setHighlightedSlots(new Set());
        
        // Only set flag to prevent click handler if we actually dragged
        if (actuallyDragged) {
            setJustFinishedDrag(true);
            setTimeout(() => setJustFinishedDrag(false), 100);
            console.log('🖱️ Blocking clicks after actual drag');
        } else {
            console.log('🖱️ No drag movement - allowing click handler');
        }
        setActuallyDragged(false);
    };

    // Add global mouse up listener to handle drag completion
    React.useEffect(() => {
        const handleGlobalMouseUp = (event) => {
            if (isDragging) {
                handleTimeSlotMouseUp(event);
            }
        };

        if (isDragging) {
            document.addEventListener('mouseup', handleGlobalMouseUp);
            document.addEventListener('mouseleave', handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('mouseleave', handleGlobalMouseUp);
        };
    }, [isDragging, dragStart, dragEnd, dragEmployee, dragDate]);

    // Original click handler for simple clicks (fallback)
    const handleTimeSlotClick = (event, employee, date, hour) => {
        // Prevent click if we just finished dragging
        if (justFinishedDrag) {
            console.log('🖱️ Ignoring click - just finished drag');
            return;
        }

        console.log('🎯 Time slot clicked - params:', { 
            employeeName: employee?.name, 
            employeeId: employee?.id,
            employee: employee,
            date, 
            hour,
            viewMode: viewMode
        });

        // Guard against undefined employee
        if (!employee || !employee.id) {
            console.error('❌ Employee is undefined or missing ID:', employee);
            return;
        }
        
        // Clear existing event state for new events
        setExistingEvent(null);
        setSelectedEmployee(employee);
        setSelectedDate(date);
        
        // Get the time slot element for positioning
        const timeSlotElement = event.currentTarget;
        
        let startTime, endTime;
        let isAllDay = false;
        
        // Check if we're in week view - if so, create all-day events
        if (viewMode === 'week') {
            console.log('📅 Week view detected - creating all-day event');
            startTime = '00:00';
            endTime = '23:59';
            isAllDay = true;
            setSelectedHour(-1); // -1 indicates all-day event
            
            console.log('🎯 FINAL selectedHour being set (week view):', -1, {
                originalHour: hour,
                finalSelectedHour: -1,
                viewMode: viewMode
            });
        } else {
            // For other views, use the original timed event logic
            // Calculate the minute position within the hour based on click position
            const minute = getMinuteFromPosition(event, timeSlotElement);
            
            // Snap to 30-minute intervals (0 or 30)
            const snappedMinute = minute < 30 ? 0 : 30;
            
            console.log('🎯 Time slot clicked:', { 
                employee: employee?.name, 
                date, 
                hour, 
                clickedMinute: minute,
                snappedMinute: snappedMinute
            });
            
            // Create start and end times based on the clicked position
            const startHour = hour;
            const startMinute = snappedMinute;
            const endMinute = snappedMinute + 30;
            
            let endHour = startHour;
            let finalEndMinute = endMinute;
            
            // If end minute goes beyond 60, move to next hour
            if (endMinute >= 60) {
                endHour = startHour + 1;
                finalEndMinute = endMinute - 60;
            }
            
            startTime = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
            endTime = `${endHour.toString().padStart(2, '0')}:${finalEndMinute.toString().padStart(2, '0')}`;
            setSelectedHour(hour + (snappedMinute / 60)); // Set actual hour with fractional minutes
            
            console.log('🎯 FINAL selectedHour being set (day view):', hour + (snappedMinute / 60), {
                originalHour: hour,
                snappedMinute: snappedMinute,
                finalSelectedHour: hour + (snappedMinute / 60),
                viewMode: viewMode
            });
        }
        
        setSelectedStartTime(startTime);
        setSelectedEndTime(endTime);
        
        console.log('🎯 Setting EventPreview for new event:', {
            startTime,
            endTime,
            isAllDay,
            employee: employee.name,
            date: date,
            viewMode: viewMode
        });
        
        // Create a new event object for preview
        const dateString = moment(date).format('YYYY-MM-DD');
        const startMoment = moment(`${dateString} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${dateString} ${endTime}`, 'YYYY-MM-DD HH:mm');
        
        // Validate the moments are valid
        if (!startMoment.isValid() || !endMoment.isValid()) {
            console.error('Invalid date/time created:', { date, startTime, endTime, dateString });
            return;
        }
        
        const newEvent = {
            title: '',
            start: startMoment.toDate(),
            end: endMoment.toDate(),
            resource: employee.id,
            isNew: true,
            isAllday: isAllDay,
            employee: employee
        };
        
        // Set the event preview position based on click position
        const rect = timeSlotElement.getBoundingClientRect();
        setEventPreviewPosition({
            x: rect.left + rect.width / 2,
            y: rect.top
        });
        
        setEventPreview(newEvent);
        
    };

    // Handler for existing event clicks
    const handleEventClick = (event, scheduleEvent) => {
        event.stopPropagation();
        
        console.log('📅 Existing event clicked:', scheduleEvent);
        
        // Set the event preview position based on click position
        const rect = event.currentTarget.getBoundingClientRect();
        setEventPreviewPosition({
            x: rect.left + rect.width / 2,
            y: rect.top
        });
        
        setEventPreview(scheduleEvent);
    };

    // Handler for event preview expand (opens full EventPopup)
    const handleEventPreviewExpand = (eventData) => {
        console.log('🔄 Expanding EventPreview to EventPopup:', {
            isNew: eventData.isNew,
            isAllday: eventData.isAllday,
            employee: eventData.employee,
            employeeEmail: eventData.employee?.email,
            employeeName: eventData.employee ? `${eventData.employee.first_name} ${eventData.employee.last_name}` : 'none',
            hasZoomInfo: !!(eventData.zoomInfo || eventData.zoom_info),
            hasParticipants: !!(eventData.participants && eventData.participants.length > 0),
            hasUploadedFiles: !!(eventData.uploadedFiles && eventData.uploadedFiles.length > 0),
            startDate: eventData.start,
            endDate: eventData.end
        });
        
        setEventPreview(null);
        
        if (eventData.isNew) {
            // For new events, create a pseudo-existing event with all the data from preview
            // This allows EventPopup to access zoom info, files, etc.
            const pseudoExistingEvent = {
                ...eventData,
                // Ensure we have the necessary fields
                title: eventData.title || '',
                description: eventData.description || '',
                location: eventData.location || '',
                participants: eventData.participants || [],
                files: eventData.uploadedFiles || [],
                zoom_info: eventData.zoomInfo || null,
                isAllday: eventData.isAllday || false, // Preserve the all-day flag
                is_all_day: eventData.isAllday || false, // Also set the server field name
                isNew: true // Keep the flag to indicate it's still a new event
            };
            
            console.log('🎯 Created pseudo-existing event for new event:', pseudoExistingEvent);
            console.log('🎯 Setting selectedEmployee to:', eventData.employee);
            
            setExistingEvent(pseudoExistingEvent);
            setSelectedEmployee(eventData.employee);
            
            // Fix date handling to avoid timezone issues
            if (eventData.start instanceof Date) {
                // Create a new Date to avoid mutation and ensure proper date handling
                const dateForPopup = new Date(eventData.start.getFullYear(), eventData.start.getMonth(), eventData.start.getDate());
                setSelectedDate(dateForPopup);
                
                // For all-day events, don't set specific times
                if (eventData.isAllday || eventData.is_all_day) {
                    setSelectedStartTime('00:00');
                    setSelectedEndTime('23:59');
                } else {
                    // Prioritize string times from EventPreview over extracted times
                    setSelectedStartTime(eventData.startTime || moment(eventData.start).format('HH:mm'));
                    setSelectedEndTime(eventData.endTime || moment(eventData.end).format('HH:mm'));
                }
            } else {
                console.warn('eventData.start is not a Date object:', eventData.start);
                setSelectedDate(new Date());
                setSelectedStartTime('09:00');
                setSelectedEndTime('10:00');
            }
        } else {
            // For existing events, set up for editing
            setExistingEvent(eventData);
            setSelectedEmployee(employees.find(emp => emp.id === parseInt(eventData.resource)));
            
            // Fix date handling for existing events too
            if (eventData.start instanceof Date) {
                const dateForPopup = new Date(eventData.start.getFullYear(), eventData.start.getMonth(), eventData.start.getDate());
                setSelectedDate(dateForPopup);
                // Prioritize string times from EventPreview over extracted times
                setSelectedStartTime(eventData.startTime || moment(eventData.start).format('HH:mm'));
                setSelectedEndTime(eventData.endTime || moment(eventData.end).format('HH:mm'));
            } else {
                console.warn('Existing event start is not a Date object:', eventData.start);
                setSelectedDate(new Date(moment(eventData.start).format('YYYY-MM-DD')));
                setSelectedStartTime(eventData.startTime || moment(eventData.start).format('HH:mm'));
                setSelectedEndTime(eventData.endTime || moment(eventData.end).format('HH:mm'));
            }
        }
        
        setIsEventPopupOpen(true);
    };

    // Handler for event preview close
    const handleEventPreviewClose = () => {
        setEventPreview(null);
        setSelectedHour(null);
    };

    // Add resize handler
    React.useEffect(() => {
        let resizeTimeout;
        
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Use more efficient selectors and cache DOM elements
                const scheduleGrid = document.querySelector('.scheduleGrid');
                const timeHeader = document.querySelector('.timeHeader');

                if (scheduleGrid && timeHeader) {
                    const newWidth = viewMode === 'week' ? '100%' : '2550px'; // Updated width
                    scheduleGrid.style.width = newWidth;
                    timeHeader.style.width = newWidth;
                }

                // Only update heights if elements exist and are visible
                const teamColumn = document.querySelector('.teamColumn');
                const scheduleSection = document.querySelector('.scheduleSection');
                
                if (teamColumn && scheduleSection && teamColumn.offsetParent !== null) {
                    teamColumn.style.height = `${scheduleSection.scrollHeight}px`;
                }
            }, 150); // Debounce by 150ms for DOM operations
        };

        handleResize(); // Call once immediately
        window.addEventListener('resize', handleResize, { passive: true });
        
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, [viewMode]);

    // Listen for AI chat calendar refresh events
    useEffect(() => {
        const handleCalendarRefresh = async (event) => {
            console.log('🔄 Calendar refresh event received:', event.detail);
            if (refreshSchedules) {
                console.log('🔄 Triggering calendar refresh from AI event...');
                
                // Try multiple refresh attempts with delays
                try {
                    await refreshSchedules();
                    console.log('✅ First refresh completed');
                    
                    // Add a second refresh after a short delay
                    setTimeout(async () => {
                        console.log('🔄 Second refresh attempt...');
                        try {
                            await refreshSchedules();
                            console.log('✅ Second refresh completed');
                        } catch (error) {
                            console.error('❌ Second refresh failed:', error);
                        }
                    }, 1000);
                    
                } catch (error) {
                    console.error('❌ Calendar refresh failed:', error);
                }
            } else {
                console.warn('⚠️ refreshSchedules function not available');
            }
        };

        // Add event listener for custom calendar refresh events
        window.addEventListener('refreshCalendar', handleCalendarRefresh);
        
        // Cleanup
        return () => {
            window.removeEventListener('refreshCalendar', handleCalendarRefresh);
        };
    }, [refreshSchedules]);

    // Get the start and end dates for the week view
    const getWeekRange = () => {
        const startOfWeek = calendarDate.clone().startOf('week');
        const endOfWeek = calendarDate.clone().endOf('week');
        return { startOfWeek, endOfWeek };
    };

    // Calculate hours between two dates
    const calculateHours = (start, end, isAllDay = false) => {
        // For all-day events, return 8 hours instead of calculating the full 24 hours
        if (isAllDay) {
            return 8;
        }
        
        const startMoment = moment(start);
        const endMoment = moment(end);
        const duration = moment.duration(endMoment.diff(startMoment));
        return Math.round(duration.asHours() * 100) / 100; // Round to 2 decimal places
    };

    // Memoized function to get schedules for a specific employee and hour
    const getSchedulesForEmployeeAndHour = useMemo(() => (employeeId, hour, date = null) => {
        const filteredSchedules = schedules.filter(schedule => {
            const scheduleStart = moment(schedule.start);
            const scheduleEnd = moment(schedule.end);
            
            // Use the provided date or current date for day view
            const targetDate = date ? moment(date) : calendarDate;
            
            // Only show events in their starting hour to avoid duplicates
            const scheduleStartHour = scheduleStart.hour();
            const isStartingHour = scheduleStartHour === hour;

            // For day view, check if the schedule is for the target date
            if (viewMode === 'day') {
                const isTargetDate = scheduleStart.isSame(targetDate, 'day');
                const resourceMatch = schedule.resource === String(employeeId);
                
                return resourceMatch && 
                       isTargetDate &&
                       isStartingHour;
            }
            
            // For week view, check if the schedule is within the current week
            const { startOfWeek, endOfWeek } = getWeekRange();
            const isThisWeek = scheduleStart.isBetween(startOfWeek, endOfWeek, 'day', '[]');
            
            return schedule.resource === String(employeeId) && 
                   isThisWeek &&
                   isStartingHour;
        });
        
        return filteredSchedules.map(schedule => {
            const scheduleStart = moment(schedule.start);
            const scheduleEnd = moment(schedule.end);
            
            // Calculate how many hours this event spans
            const startHour = scheduleStart.hour();
            const endHour = scheduleEnd.hour();
            const startMinute = scheduleStart.minute();
            const endMinute = scheduleEnd.minute();
            
            // Calculate total duration in hours
            const totalHours = endHour - startHour + (endMinute > 0 ? 1 : 0);
            
            return {
                ...schedule,
                startMinute,
                endMinute,
                totalHours,
                startHour,
                endHour,
                isMultiHour: totalHours > 1
            };
        });
    }, [schedules, viewMode, calendarDate]);

    const saveScheduleToServer = async (schedule) => {
        try {
            // Format the schedule data for the server
            const serverSchedule = {
                title: schedule.title || 'Work Hours',
                shift_date: moment(schedule.start).format('YYYY-MM-DD'),
                start_time: moment(schedule.start).format('HH:mm:ss'),
                end_time: moment(schedule.end).format('HH:mm:ss'),
                calendar_type: schedule.calendarId || 'shift',
                is_all_day: schedule.isAllDay || false,
                user_id: schedule.resource?.id,
                company_id: schedule.resource?.company_id,
                description: schedule.description || '',
                location: schedule.location || '',
                color: schedule.backgroundColor || schedule.color || '#4263eb',
                type: 'work'
            };

            console.log('Sending schedule data:', serverSchedule);

            // Use the API utility which handles token validation
            const response = await api.post('/api/schedules', serverSchedule);
            
            console.log('Schedule saved successfully:', response.data);

            // Then automatically record hours for the new schedule
            if (response.data.calendar_type === 'shift' || response.data.type === 'work') {
                try {
                    const eventForHours = {
                        ...response.data,
                        start: moment(`${response.data.shift_date} ${response.data.start_time}`).toDate(),
                        end: moment(`${response.data.shift_date} ${response.data.end_time}`).toDate(),
                        user_id: response.data.user_id,
                        company_id: response.data.company_id
                    };

                    console.log('Recording hours for new schedule:', eventForHours);
                    
                    await recordHours(eventForHours);
                    console.log('Hours automatically recorded for new schedule');
                } catch (hourError) {
                    console.error('Failed to record hours for new schedule:', hourError);
                    // Don't throw the error - we still want to return the saved schedule
                }
            } else {
                console.log('Skipping hours recording - not a work/shift event:', {
                    calendar_type: response.data.calendar_type,
                    type: response.data.type
                });
            }

            return response.data;
        } catch (error) {
            console.error('Error saving schedule:', {
                error: error.message,
                response: error.response?.data,
                schedule: schedule
            });
            throw error;
        }
    };

    const updateScheduleOnServer = async (schedule) => {
        try {
            // Format the schedule data for the server
            const serverSchedule = {
                title: schedule.title || 'Work Hours',
                shift_date: moment(schedule.start).format('YYYY-MM-DD'),
                start_time: moment(schedule.start).format('HH:mm:ss'),
                end_time: moment(schedule.end).format('HH:mm:ss'),
                calendar_type: schedule.calendarId || 'shift',
                is_all_day: schedule.isAllDay || false,
                user_id: parseInt(schedule.resource),
                company_id: schedule.company_id,
                description: schedule.description || '',
                location: schedule.location || '',
                color: schedule.backgroundColor || schedule.color || '#4263eb',
                type: 'work'
            };

            console.log('Updating schedule data:', serverSchedule);

            // Use the API utility which handles token validation
            const response = await api.put(`/api/schedules/${schedule.id}`, serverSchedule);
            
            console.log('Schedule updated successfully:', response.data);

            return response.data;
        } catch (error) {
            console.error('Error updating schedule:', {
                error: error.message,
                response: error.response?.data,
                schedule: schedule
            });
            throw error;
        }
    };

    const recordHours = async (event) => {
        try {
            const hours = calculateHours(event.start, event.end, event.is_all_day);
            const startTime = moment(event.start);
            const endTime = moment(event.end);

            console.log('Recording hours for event:', {
                eventId: event.id,
                title: event.title,
                start: startTime.format('YYYY-MM-DD HH:mm:ss'),
                end: endTime.format('YYYY-MM-DD HH:mm:ss'),
                calculatedHours: hours,
                is_all_day: event.is_all_day
            });

            const requestData = {
                user_id: event.user_id,
                event_id: event.id,
                work_date: startTime.format('YYYY-MM-DD'),
                hours_worked: hours,
                company_id: event.company_id,
                start_time: startTime.format('HH:mm:ss'),
                end_time: endTime.format('HH:mm:ss'),
                source: 'calendar'
            };

            console.log('Sending hours recording request:', requestData);

            // Use the API utility which handles token validation
            const recordResponse = await api.post('/api/schedules/hours', requestData);
            console.log('Hours recorded successfully:', recordResponse.data);

            // Verify the hours were recorded
            const verifyResponse = await api.get('/api/hours/summary', {
                params: {
                    user_id: event.user_id,
                    company_id: event.company_id,
                    start_date: startTime.format('YYYY-MM-DD'),
                    end_date: endTime.format('YYYY-MM-DD')
                }
            });
            console.log('Hours verification response:', verifyResponse.data);

            // Trigger a refresh of the hours summary
            if (window.refreshHoursSummary) {
                console.log('Triggering hours summary refresh after recording');
                window.refreshHoursSummary();
            } else {
                console.warn('refreshHoursSummary function not found');
            }

            return recordResponse.data;
        } catch (error) {
            console.error('Error recording hours:', {
                error: error.message,
                response: error.response?.data,
                event: {
                    id: event.id,
                    title: event.title,
                    start: event.start,
                    end: event.end
                }
            });
            throw error;
        }
    };

    const calculateMonthlyHours = (employeeId) => {
        const currentMonth = moment().format('YYYY-MM');
        return schedules
            .filter(schedule => {
                const scheduleMonth = moment(schedule.start).format('YYYY-MM');
                return schedule.resource === String(employeeId) && scheduleMonth === currentMonth;
            })
            .reduce((total, schedule) => {
                // Only count hours for:
                // 1. Regular work schedules (no countsTowardHours property)
                // 2. PTO requests (countsTowardHours === true)
                // Skip regular time-off requests (countsTowardHours === false)
                if (schedule.hasOwnProperty('countsTowardHours') && !schedule.countsTowardHours) {
                    return total; // Skip this event - it's regular time-off, not PTO
                }
                
                const hours = calculateHours(schedule.start, schedule.end, schedule.is_all_day);
                return total + hours;
            }, 0);
    };

    const calculateTotalHours = () => {
        const currentMonth = moment().format('YYYY-MM');
        
        // Get the IDs of the currently filtered employees
        const filteredEmployeeIds = employees.map(emp => String(emp.id));
        
        return Math.round(schedules.reduce((total, schedule) => {
            // Only include schedules for filtered employees and current month
            if (moment(schedule.start).format('YYYY-MM') === currentMonth && 
                filteredEmployeeIds.includes(String(schedule.resource))) {
                return total + calculateHours(schedule.start, schedule.end, schedule.is_all_day);
            }
            return total;
        }, 0));
    };

    const totalHours = calculateTotalHours();
    const totalTarget = employees.length * 200; // 200 hours per employee
    const weeklyTarget = employees.length * 40; // 40 hours per employee per week

    // Additional calculation functions for different time periods
    const calculateDailyHours = () => {
        const today = moment().format('YYYY-MM-DD');
        const filteredEmployeeIds = employees.map(emp => String(emp.id));
        
        return Math.round(schedules.reduce((total, schedule) => {
            if (moment(schedule.start).format('YYYY-MM-DD') === today && 
                filteredEmployeeIds.includes(String(schedule.resource))) {
                return total + calculateHours(schedule.start, schedule.end, schedule.is_all_day);
            }
            return total;
        }, 0));
    };

    const calculateTTMHours = () => {
        const twelveMonthsAgo = moment().subtract(12, 'months');
        const filteredEmployeeIds = employees.map(emp => String(emp.id));
        
        return Math.round(schedules.reduce((total, schedule) => {
            if (moment(schedule.start).isAfter(twelveMonthsAgo) && 
                filteredEmployeeIds.includes(String(schedule.resource))) {
                return total + calculateHours(schedule.start, schedule.end, schedule.is_all_day);
            }
            return total;
        }, 0));
    };

    const calculateYearlyHours = () => {
        const currentYear = moment().format('YYYY');
        const filteredEmployeeIds = employees.map(emp => String(emp.id));
        
        return Math.round(schedules.reduce((total, schedule) => {
            if (moment(schedule.start).format('YYYY') === currentYear && 
                filteredEmployeeIds.includes(String(schedule.resource))) {
                return total + calculateHours(schedule.start, schedule.end, schedule.is_all_day);
            }
            return total;
        }, 0));
    };

    // Calculate targets for additional time periods
    const dailyTarget = employees.length * 8; // 8 hours per employee per day
    const ttmTarget = employees.length * 2400; // 200 hours per month * 12 months
    const yearlyTarget = employees.length * 2080; // Standard work year hours

    // Calculate values for enabled progress bars
    const dailyHours = progressBarSettings.showDaily ? calculateDailyHours() : 0;
    const ttmHours = progressBarSettings.showTTM ? calculateTTMHours() : 0;
    const yearlyHours = progressBarSettings.showYearly ? calculateYearlyHours() : 0;

    const getProgressBarColor = (percentage) => {
        if (percentage < 70) return '#348944'; // green
        if (percentage < 90) return '#FFAE00'; // yellow
        return '#EC1817'; // red
    };

    const weeklyProgressWidth = Math.min((totalHours / weeklyTarget) * 100, 100);
    const monthlyProgressWidth = Math.min((totalHours / totalTarget) * 100, 100);
    
    // Additional progress widths
    const dailyProgressWidth = Math.min((dailyHours / dailyTarget) * 100, 100);
    const ttmProgressWidth = Math.min((ttmHours / ttmTarget) * 100, 100);
    const yearlyProgressWidth = Math.min((yearlyHours / yearlyTarget) * 100, 100);

    const getRandomPastelColor = (seed) => {
        const colors = [
            '#2196F3', // blue
            '#9C27B0', // purple
            '#4CAF50', // green
            '#FF9800', // orange
            '#E91E63', // pink
            '#00BCD4', // cyan
            '#009688', // teal
            '#607D8B'  // blue gray
        ];
        const index = Math.abs(seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colors.length;
        return colors[index];
    };

    const getDarkerColor = (color) => {
        try {
            if (!color) return '#163c8d';
            
            // Remove any leading # if present
            const hex = color.replace('#', '');
            
            // Ensure we have a valid 6-character hex
            if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return '#163c8d';
            
            // Convert to RGB
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            
            // Make it darker by reducing each component by 60% (more subtle)
            const darkerR = Math.max(Math.floor(r * 0.4), 1);
            const darkerG = Math.max(Math.floor(g * 0.4), 1);
            const darkerB = Math.max(Math.floor(b * 0.4), 1);
            
            // Convert back to hex
            return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
        } catch (error) {
            console.error('Error creating darker color:', error);
            return '#163c8d';
        }
    };

    const getLighterColor = (color) => {
        try {
            if (!color) return '#e3f2fd';
            
            let r, g, b;
            
            // Handle rgb/rgba format
            if (color.startsWith('rgb')) {
                const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (rgbMatch) {
                    r = parseInt(rgbMatch[1]);
                    g = parseInt(rgbMatch[2]);
                    b = parseInt(rgbMatch[3]);
                } else {
                    return '#e3f2fd';
                }
            } else {
                // Handle hex format
                const hex = color.replace('#', '');
                
                // Ensure we have a valid 6-character hex
                if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return '#e3f2fd';
                
                // Convert to RGB
                r = parseInt(hex.substring(0, 2), 16);
                g = parseInt(hex.substring(2, 4), 16);
                b = parseInt(hex.substring(4, 6), 16);
            }
            
            // Mix with white (255, 255, 255) to create a lighter version
            // Using 85% white + 15% original color for a very light tint
            const mixRatio = 0.15;
            const lighterR = Math.round(r * mixRatio + 255 * (1 - mixRatio));
            const lighterG = Math.round(g * mixRatio + 255 * (1 - mixRatio));
            const lighterB = Math.round(b * mixRatio + 255 * (1 - mixRatio));
            
            // Convert back to hex
            return `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;
        } catch (error) {
            console.error('Error creating lighter color:', error);
            return '#e3f2fd';
        }
    };

    const renderAvatar = (employee) => {
        const firstName = employee.first_name || '';
        const lastName = employee.last_name || '';
        const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 
                        employee.email?.charAt(0)?.toUpperCase() || 'U';
        const backgroundColor = getRandomPastelColor(employee.email);
        const profilePicture = employee.profile_picture_url;
        
        if (profilePicture) {
            return (
                <div style={{
                    ...styles.avatar,
                    backgroundColor: 'transparent',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    <img 
                        src={profilePicture} 
                        alt={`${firstName} ${lastName}`} 
                        style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '12px'
                        }}
                        onError={(e) => {
                            console.error('Avatar image failed to load:', profilePicture);
                            // Hide the image and show initials fallback
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling;
                            if (fallback) {
                                fallback.style.display = 'flex';
                            }
                        }}
                    />
                    <div style={{
                        ...styles.avatar,
                        backgroundColor,
                        color: 'white',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        display: 'none'
                    }}>
                        {initials}
                    </div>
                </div>
            );
        }
        
        return (
            <div style={{
                ...styles.avatar,
                backgroundColor,
                color: 'white'
            }}>
                {initials}
            </div>
        );
    };

    // Add handler for prefilling participants
    const handlePrefillParticipant = (employee, event) => {
        event.stopPropagation(); // Prevent any parent click handlers
        console.log('🎯 Prefilling participant:', employee);
        
        // Check if employee is already selected
        const isAlreadySelected = prefilledParticipants.some(p => p.id === employee.id);
        
        if (isAlreadySelected) {
            // If already selected, remove them
            setPrefilledParticipants(prev => prev.filter(p => p.id !== employee.id));
            setPrefilledEmployeeId(null); // Clear the single employee ID since we're using multiple
        } else {
            // If not selected, add them to the array
            setPrefilledParticipants(prev => [...prev, employee]);
            setPrefilledEmployeeId(null); // Clear the single employee ID since we're using multiple
        }
        
        // Provide visual feedback that the participant has been selected
        const button = event.currentTarget;
        const originalBg = button.style.backgroundColor;
        const originalBorder = button.style.borderColor;
        
        // Flash green for adding, red for removing
        button.style.backgroundColor = isAlreadySelected ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)';
        button.style.borderColor = isAlreadySelected ? 'rgba(239, 68, 68, 0.5)' : 'rgba(34, 197, 94, 0.5)';
        
        setTimeout(() => {
            button.style.backgroundColor = originalBg;
            button.style.borderColor = originalBorder;
        }, 200);
        
        console.log('🎯 Updated prefilled participants:', isAlreadySelected ? 'removed' : 'added', employee.email);
    };

    // Handler for progress bar settings
    const handleProgressBarToggle = (type) => {
        setProgressBarSettings(prev => {
            const newSettings = {
                ...prev,
                [type]: !prev[type]
            };
            // Save to localStorage
            localStorage.setItem('progressBarSettings', JSON.stringify(newSettings));
            return newSettings;
        });
    };

    const handleEventSave = async (eventData) => {
        try {
            console.log('💾 Saving event data:', {
                title: eventData.title,
                start: eventData.start,
                end: eventData.end,
                filesCount: eventData.files ? eventData.files.length : 0,
            });

            // Get the employee based on the selected resource
            const employee = employees.find(emp => emp.id === parseInt(eventData.resource));
            if (!employee) {
                throw new Error('Employee not found for the selected resource');
            }

            // Get user_id and company_id from the employee
            const user_id = employee.user_id;
            const company_id = employee.company_id;

            console.log('👤 Employee details:', { user_id, company_id, email: employee.email });

            // Format dates for server - handle both Date objects and time strings
            let startDate, endDate;
            
            // For all-day events, prioritize the string times over Date objects
            if (eventData.is_all_day && eventData.start_time && eventData.end_time) {
                console.log('📅 All-day event detected, using string times:', {
                    start_time: eventData.start_time,
                    end_time: eventData.end_time
                });
                
                const baseDate = selectedDate ? (selectedDate instanceof Date ? selectedDate : new Date(selectedDate)) : new Date();
                
                // Parse the correct time strings for all-day events
                const [startHour, startMinute, startSecond] = eventData.start_time.split(':').map(Number);
                const [endHour, endMinute, endSecond] = eventData.end_time.split(':').map(Number);
                
                startDate = new Date(baseDate);
                startDate.setHours(startHour, startMinute, startSecond || 0, 0);
                
                endDate = new Date(baseDate);
                endDate.setHours(endHour, endMinute, endSecond || 0, 0);
                
                console.log('📅 Created correct all-day Date objects:', {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                });
            } else if (eventData.start instanceof Date && eventData.end instanceof Date) {
                // If we have Date objects, use them directly
                startDate = eventData.start;
                endDate = eventData.end;
                console.log('📅 Using Date objects directly');
            } else if (typeof eventData.start === 'string' && typeof eventData.end === 'string') {
                // If we have time strings, combine with selectedDate
                console.log('📅 Processing time strings with selectedDate:', {
                    selectedDate: selectedDate,
                    selectedDateType: typeof selectedDate,
                    selectedDateIsDate: selectedDate instanceof Date,
                    eventStart: eventData.start,
                    eventEnd: eventData.end
                });
                
                const baseDate = selectedDate ? (selectedDate instanceof Date ? selectedDate : new Date(selectedDate)) : new Date();
                
                // Validate baseDate is valid
                if (isNaN(baseDate.getTime())) {
                    console.error('❌ Invalid baseDate created:', { selectedDate, baseDate });
                    throw new Error(`Invalid base date: ${selectedDate}`);
                }
                
                // Parse time strings like "09:30" or "10:00"
                const [startHour, startMinute] = eventData.start.split(':').map(Number);
                const [endHour, endMinute] = eventData.end.split(':').map(Number);
                
                startDate = new Date(baseDate);
                startDate.setHours(startHour, startMinute, 0, 0);
                
                endDate = new Date(baseDate);
                endDate.setHours(endHour, endMinute, 0, 0);
                
                console.log('📅 Combined date and time strings:', {
                    baseDate: baseDate.toISOString(),
                    startTime: eventData.start,
                    endTime: eventData.end,
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                });
            } else {
                throw new Error('Invalid date format in event data');
            }

            // Validate the dates
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                throw new Error('Invalid date/time values');
            }

            // Create schedule data object
            const scheduleData = {
                title: eventData.title,
                shift_date: startDate.toISOString().split('T')[0],
                end_date: endDate.toISOString().split('T')[0],
                start_time: startDate.toLocaleTimeString('en-US', { hour12: false }),
                end_time: endDate.toLocaleTimeString('en-US', { hour12: false }),
                is_all_day: eventData.isAllDay || eventData.isAllday || false,
                calendar_type: eventData.calendarId || 'default',
                user_id: user_id,
                company_id: company_id,
                resource: eventData.resource,
                color: eventData.backgroundColor || eventData.color || '#4b8dff',
                recurrence_rule: eventData.recurrence_rule || null,
                zoom_info: eventData.zoom_info || null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                participants: eventData.participants ? eventData.participants.map(p => p.email).filter(Boolean) : []
            };

            // Auto-detect all-day events based on time pattern
            if (scheduleData.start_time === '00:00:00' && scheduleData.end_time === '23:59:59') {
                scheduleData.is_all_day = true;
                console.log('🎯 Auto-detected all-day event based on time pattern');
            }

            console.log('🎯 Schedule data being sent to server:', {
                title: scheduleData.title,
                start_time: scheduleData.start_time,
                end_time: scheduleData.end_time,
                is_all_day: scheduleData.is_all_day,
                eventData_isAllDay: eventData.isAllDay,
                eventData_isAllday: eventData.isAllday,
                recurrence_rule: scheduleData.recurrence_rule
            });

            let response;

            // Check if this is an update to an existing event
            if (existingEvent && existingEvent.id) {
                console.log('🔄 Updating existing event:', existingEvent.id);
                
                if (eventData.files && eventData.files.length > 0) {
                    // Handle file uploads for updates...
                    // [Previous file upload code remains unchanged]
                } else {
                    // Update without files
                    response = await api.put(`/api/schedules/${existingEvent.id}`, scheduleData);
                    console.log('✅ Schedule updated:', response.data);
                }
            } else {
                console.log('➕ Creating new event');
                
                // Check if we have files to upload
                const hasFiles = eventData.files && eventData.files.length > 0;
                
                if (hasFiles) {
                    console.log('📎 Files detected, using FormData for upload');
                    
                    // Create FormData object for file uploads
                    const formData = new FormData();
                    
                    // Add all schedule data to FormData
                    Object.entries(scheduleData).forEach(([key, value]) => {
                        if (value !== null && value !== undefined) {
                            if (Array.isArray(value)) {
                                formData.append(key, JSON.stringify(value));
                            } else if (typeof value === 'object') {
                                formData.append(key, JSON.stringify(value));
                            } else {
                                formData.append(key, value);
                            }
                        }
                    });
                    
                    // Add files to FormData
                    eventData.files.forEach((file) => {
                        formData.append('files', file.file || file);
                    });
                    
                    // Add file metadata
                    formData.append('fileMetadata', JSON.stringify(eventData.files.map(file => ({
                        originalName: file.name,
                        type: file.type,
                        size: file.size,
                        lastModified: file.lastModified
                    }))));
                    
                    // Use fetch instead of axios for FormData
                    const token = localStorage.getItem('token');
                    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
                    
                    const fetchResponse = await fetch(`${API_BASE_URL}/api/schedules`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                        },
                        body: formData
                    });
                    
                    if (!fetchResponse.ok) {
                        const responseText = await fetchResponse.text();
                        console.error('Server response:', fetchResponse.status, responseText);
                        throw new Error(`Failed to save schedule: ${responseText}`);
                    }
                    
                    const savedSchedule = await fetchResponse.json();
                    console.log('✅ Schedule with files created:', savedSchedule);
                    response = { id: savedSchedule.id };
                    
                } else {
                    // No files, use regular JSON API call
                    console.log('📄 No files, using JSON API call');
                    
                    // Create all schedules (main + participants) directly via API
                    const allSchedulesToCreate = [];
                    
                    // Add the main schedule
                    allSchedulesToCreate.push({
                        scheduleData: scheduleData,
                        employee: employee,
                        isMain: true
                    });
                    
                    // Add participant schedules
                    if (eventData.participants && eventData.participants.length > 0) {
                        console.log('🔄 Preparing participant events...');
                        console.log('🎭 Participant data received:', eventData.participants);
                        
                        for (const participant of eventData.participants) {
                            console.log('🎭 Processing participant:', participant);
                            
                            // Handle both email strings and participant objects
                            const participantEmail = typeof participant === 'string' ? participant : participant.email;
                            
                            if (!participantEmail) {
                                console.warn(`⚠️ Participant email is missing:`, participant);
                                continue;
                            }
                            
                            // Find the full employee object from the employees array using email
                            const participantEmployee = employees.find(emp => emp.email === participantEmail);
                            
                            if (!participantEmployee) {
                                console.warn(`⚠️ Participant employee not found for email: ${participantEmail}`);
                                continue;
                            }
                            
                            console.log('🎭 Found participant employee:', {
                                id: participantEmployee.id,
                                user_id: participantEmployee.user_id,
                                email: participantEmployee.email,
                                name: `${participantEmployee.first_name} ${participantEmployee.last_name}`
                            });
                            
                            // Skip if participant is the same as the main resource
                            if (participantEmployee.id === parseInt(eventData.resource)) {
                                console.log(`⏭️ Skipping participant ${participantEmployee.email} - same as main resource`);
                                continue;
                            }

                            // Create participant schedule data with correct user_id mapping
                            const participantScheduleData = {
                                ...scheduleData,
                                user_id: participantEmployee.user_id, // Use the employee's user_id
                                resource: String(participantEmployee.id) // Use the employee's id as resource
                            };

                            allSchedulesToCreate.push({
                                scheduleData: participantScheduleData,
                                employee: participantEmployee, // Use the full employee object
                                isMain: false
                            });
                        }
                    }
                    
                    console.log(`🔄 Creating ${allSchedulesToCreate.length} total schedules (1 main + ${allSchedulesToCreate.length - 1} participants)`);
                    
                    // Create all schedules
                    for (const { scheduleData: data, employee: emp, isMain } of allSchedulesToCreate) {
                        try {
                            console.log(`🔄 Creating ${isMain ? 'main' : 'participant'} event for: ${emp.email}`);
                            const apiResponse = await api.post('/api/schedules', data);
                            console.log(`✅ ${isMain ? 'Main' : 'Participant'} schedule created for ${emp.email}:`, apiResponse.data);

                            if (isMain) {
                                response = { id: apiResponse.data.id };
                            }
                        } catch (error) {
                            console.error(`❌ Failed to create schedule for ${isMain ? 'main' : 'participant'} ${emp.email}:`, error);
                            if (isMain) {
                                throw error; // If main event fails, throw error
                            }
                        }
                    }
                }

                // After all schedules are created, refresh the schedules from the server
                console.log('🔄 Refreshing schedules after creation...');
                await refreshSchedules();
            }

            return response;
        } catch (error) {
            console.error('Error saving schedule:', error);
            throw error;
        }
    };

    const handleEventDelete = async (eventId) => {
        try {
            console.log('🗑️ Deleting event with ID:', eventId);
            
            // For recurring instances, we need to extract the original event ID
            let actualEventId = eventId;
            
            // Check if this is a recurring instance ID (contains underscore and date)
            if (typeof eventId === 'string' && eventId.includes('_')) {
                // Extract the original event ID from the recurring instance ID
                actualEventId = eventId.split('_')[0];
                console.log('📅 Recurring instance detected, using original event ID:', actualEventId);
            }
            
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await api.delete(`/api/schedules/${actualEventId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200) {
                console.log('✅ Event deleted successfully');
                // Close the EventPreview and EventPopup
                setEventPreview(null);
                setIsEventPopupOpen(false);
                // Refresh schedules from server instead of managing state locally
                console.log('🔄 Refreshing schedules after deletion...');
                await refreshSchedules();
            } else {
                throw new Error(`Failed to delete event: ${response.status}`);
            }
        } catch (error) {
            console.error('❌ Error deleting event:', error);
            throw error;
        }
    };

    const handleExistingEventClick = useCallback(async (schedule, employee, date) => {
        try {
            // First, set the basic event data
            setExistingEvent(schedule);
            setSelectedEmployee(employee);
            setSelectedDate(date);
            
            // Set the start and end times from the schedule
            if (schedule.is_all_day) {
                console.log('Setting all-day times in handleExistingEventClick for schedule:', schedule.id);
                setSelectedStartTime('00:00');
                setSelectedEndTime('23:59');
            } else {
                const startTime = moment(schedule.start).format('HH:mm');
                const endTime = moment(schedule.end).format('HH:mm');
                
                setSelectedStartTime(startTime);
                setSelectedEndTime(endTime);
            }

            // Check if files are missing and fetch them if needed
            if (!schedule.files || schedule.files.length === 0) {
                console.log('Files missing from schedule data, fetching from server...');
                try {
                    const token = localStorage.getItem('token');
                    if (token) {
                        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
                        const response = await fetch(`${API_BASE_URL}/api/schedules/${schedule.id}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                        
                        if (response.ok) {
                            const fullEventData = await response.json();
                            console.log('Fetched full event data:', fullEventData);
                            if (fullEventData.files && fullEventData.files.length > 0) {
                                // Update the schedule with files
                                const updatedSchedule = { ...schedule, files: fullEventData.files };
                                setExistingEvent(updatedSchedule);
                                console.log('Added files to schedule:', fullEventData.files);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error fetching event files:', error);
                }
            }
            
            setIsEventPopupOpen(true);
        } catch (error) {
            console.error('Error handling existing event click:', error);
            // Still open the popup even if file fetching fails
            setIsEventPopupOpen(true);
        }
    }, []);

    // Helper functions to check for Zoom meetings and participant files
    const hasZoomMeeting = useCallback((schedule) => {
        return schedule.zoom_info && (
            schedule.zoom_info.join_url || 
            schedule.zoom_info.joinUrl ||
            schedule.zoom_info.meeting_id ||
            schedule.zoom_info.id
        );
    }, []);

    const hasParticipantFiles = useCallback((schedule) => {
        return schedule.files && Array.isArray(schedule.files) && schedule.files.length > 0;
    }, []);

    const renderEvent = useCallback((schedule, employee, date, hour) => {
        const eventColor = schedule.color || schedule.backgroundColor || '#4b8dff';
        
        // Convert hex to RGB for background color - theme aware
        const getRGBBackground = (hex) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            
            // Check if we're in dark mode by checking data-theme attribute
            const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
            
            if (isDarkMode) {
                // Dark mode: use darker background (multiply by 0.2)
                return `rgb(${Math.floor(r * 0.2)}, ${Math.floor(g * 0.2)}, ${Math.floor(b * 0.2)})`;
            } else {
                // Light mode: use lighter background (mix with white)
                const lightR = Math.floor(r * 0.2 + 255 * 0.8);
                const lightG = Math.floor(g * 0.2 + 255 * 0.8);
                const lightB = Math.floor(b * 0.2 + 255 * 0.8);
                return `rgb(${lightR}, ${lightG}, ${lightB})`;
            }
        };
        
        const bgColor = eventColor.startsWith('#') ? getRGBBackground(eventColor) : eventColor;
        
        // Calculate position and size based on minutes within the hour
        const startMinute = schedule.startMinute || 0;
        const endMinute = schedule.endMinute || 60;
        const totalHours = schedule.totalHours || 1;
        const isMultiHour = schedule.isMultiHour || false;
        
        // Format time display
        const formatTime = (date) => {
            if (!date) return '';
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const period = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            const displayMinutes = minutes.toString().padStart(2, '0');
            return `${displayHours}:${displayMinutes}${period}`;
        };
        
        // Enhanced all-day detection with multiple methods and view-specific logic
        const allDayChecks = {
            databaseFlag: schedule.is_all_day === true,
            timeStrings: schedule.start_time === '00:00' && schedule.end_time === '23:59',
            dateObjects: schedule.start && schedule.end && 
                       moment(schedule.start).format('HH:mm') === '00:00' && 
                       moment(schedule.end).format('HH:mm') === '23:59'
        };
        
        const shouldBeAllDay = allDayChecks.databaseFlag || allDayChecks.timeStrings || allDayChecks.dateObjects;
        
        // Only show "All Day" in week view, show times in day view
        let timeDisplay;
        if (shouldBeAllDay && viewMode === 'week') {
            timeDisplay = 'All Day';
            console.log('📅 All-day event detected for display in week view:', schedule.id);
        } else {
            // Show actual times for day view or non-all-day events
            timeDisplay = `${formatTime(moment(schedule.start).toDate())} - ${formatTime(moment(schedule.end).toDate())}`;
        }
        
        // Common styles for both views
        const eventStyles = {
            background: bgColor,
            backgroundColor: bgColor,
            color: eventColor,
            border: `2px solid ${eventColor}`,
            borderRadius: '6px',
            fontSize: '12px',
            cursor: 'pointer',
            overflow: 'hidden',
            zIndex: 1,
            boxShadow: `inset 0 0 0 1000px ${bgColor}`, // Force background fill
            WebkitBoxShadow: `inset 0 0 0 1000px ${bgColor}`, // For Safari
            MozBoxShadow: `inset 0 0 0 1000px ${bgColor}`, // For Firefox
        };
        
        if (viewMode === 'week') {
            // Week view - simpler positioning
            return (
                <div
                    key={schedule.id}
                    style={{
                        ...eventStyles,
                        position: 'relative',
                        height: '60px',
                        width: '100%',
                        marginBottom: '4px',
                        cursor: draggingEvent?.id === schedule.id ? 'grabbing' : 'grab',
                    }}
                    draggable={true}
                    onMouseDown={(event) => {
                        // Check if it's a left click
                        if (event.button === 0) {
                            handleEventDragStart(event, schedule);
                        }
                    }}
                    onClick={(event) => {
                        // Only handle click if we're not dragging
                        if (!draggingEvent) {
                            handleEventClick(event, schedule);
                        }
                    }}
                    onDragStart={(e) => e.preventDefault()} // Prevent default HTML5 drag
                >
                    <div style={{
                        padding: '6px 8px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 1,
                        fontFamily: 'Inter, sans-serif',
                        height: '100%',
                        color: eventColor,
                        background: bgColor,
                        boxShadow: `inset 0 0 0 1000px ${bgColor}`,
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            width: '100%',
                        }}>
                            <div style={{
                                flex: 1,
                                paddingRight: '4px',
                            }}>
                                <div style={{
                                    fontSize: '14px',  // Increased from 12px
                                    fontWeight: '600',
                                    lineHeight: '1.3', // Slightly increased line height
                                    marginBottom: timeDisplay ? '4px' : '0', // Increased spacing
                                    color: eventColor,
                                    background: bgColor,
                                }}>
                                    {schedule.title || 'Work Hours'}
                                </div>
                                {timeDisplay && (
                                    <div style={{
                                        fontSize: '12px',  // Increased from 10px
                                        fontWeight: '500', // Made slightly bolder
                                        opacity: 0.9,      // Made more visible
                                        lineHeight: '1.2', // Slightly increased line height
                                        color: eventColor,
                                        background: bgColor,
                                    }}>
                                        {timeDisplay}
                                    </div>
                                )}
                            </div>
                            {/* Event Icons */}
                            {(hasZoomMeeting(schedule) || hasParticipantFiles(schedule) || (schedule.participants && schedule.participants.length > 0) || schedule.isRecurringInstance) && (
                                <div style={{
                                    display: 'flex',
                                    gap: '2px',
                                    marginTop: '2px',
                                }}>
                                    {schedule.isRecurringInstance && (
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '12px',
                                            background: eventColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                        }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                                <path d="M12 4V1L8 5L12 9V6C15.31 6 18 8.69 18 12C18 13.01 17.75 13.97 17.3 14.8L18.76 16.26C19.54 15.03 20 13.57 20 12C20 7.58 16.42 4 12 4ZM12 18C8.69 18 6 15.31 6 12C6 10.99 6.25 10.03 6.7 9.2L5.24 7.74C4.46 8.97 4 10.43 4 12C4 16.42 7.58 20 12 20V23L16 19L12 15V18Z"/>
                                            </svg>
                                        </div>
                                    )}
                                    {hasZoomMeeting(schedule) && (
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '12px',
                                            background: eventColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                        }}>
                                            <svg width="10" height="6.5" viewBox="0 0 20 13" fill="rgba(255, 255, 255, 0.9)" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M2.75 0C2.02065 0 1.32118 0.289731 0.805456 0.805456C0.289731 1.32118 0 2.02065 0 2.75V10.25C0 10.9793 0.289731 11.6788 0.805456 12.1945C1.32118 12.7103 2.02065 13 2.75 13H11.25C11.9793 13 12.6788 12.7103 13.1945 12.1945C13.7103 11.6788 14 10.9793 14 10.25V8.656L17.419 11.701C18.224 12.418 19.5 11.846 19.5 10.767V1.865C19.5 0.785 18.224 0.214 17.419 0.931L14 3.976V2.75C14 2.02065 13.7103 1.32118 13.1945 0.805456C12.6788 0.289731 11.9793 0 11.25 0H2.75Z"/>
                                            </svg>
                                        </div>
                                    )}
                                    {hasParticipantFiles(schedule) && (
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '12px',
                                            background: eventColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                        }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"/>
                                            </svg>
                                        </div>
                                    )}
                                    {(schedule.participants && schedule.participants.length > 0) && (
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '12px',
                                            background: eventColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                        }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        
        // Day view - calculate actual pixel values using state dimensions
        const cellPadding = 4;
        
        // Calculate actual number of displayed employees (including role headers)
        const displayedEmployeeCount = groupedEmployees.reduce((total, group) => total + group.employees.length, 0);
        const roleHeaderCount = groupedEmployees.length;
        const totalDisplayRows = displayedEmployeeCount + roleHeaderCount;
        
        // Calculate actual cell height: use measured height from DOM
        const usableHeight = Math.max(actualCellHeight - 25, 25); // Even more conservative padding for better fit
        
        // Calculate top position based on start minute
        let topPosition = cellPadding + (startMinute / 60) * usableHeight;
        
        // In day view, adjust 30-minute events to be positioned slightly lower
        if (viewMode === 'day' && startMinute === 30) {
            topPosition += 16; // Push 30-minute events down by 16px
        }
        
        // Calculate event height based on duration
        const duration = endMinute - startMinute;
        let eventHeight = (duration / 60) * usableHeight;
        
        // Make events thicker by applying a responsive thickness multiplier
        // Use even less thickness when there are more team members to ensure proper fit
        const thicknessMultiplier = totalDisplayRows > 5 ? 1.0 : 1.2;
        eventHeight = eventHeight * thicknessMultiplier;
        
        // Ensure minimum height for visibility
        const minHeight = totalDisplayRows > 5 ? 15 : 20;
        eventHeight = Math.max(eventHeight, minHeight);
        
        // For multi-hour events, calculate width to span multiple cells
        let eventWidth = `calc(100% - ${cellPadding * 2}px)`;
        if (isMultiHour) {
            const cellWidth = 150; // Width of each hour cell
            eventWidth = `${totalHours * cellWidth - (cellPadding * 2)}px`;
        }
        
        return (
            <div
                key={`${schedule.id}-${teamMemberLimit}-${totalDisplayRows}`}
                style={{
                    ...eventStyles,
                    position: 'absolute',
                    top: `${topPosition}px`,
                    left: `${cellPadding}px`,
                    height: `${eventHeight}px`,
                    width: eventWidth,
                    cursor: draggingEvent?.id === schedule.id ? 'grabbing' : 'grab',
                }}
                draggable={true}
                onMouseDown={(event) => {
                    // Check if it's a left click
                    if (event.button === 0) {
                        handleEventDragStart(event, schedule);
                    }
                }}
                onClick={(event) => {
                    // Only handle click if we're not dragging
                    if (!draggingEvent) {
                        handleEventClick(event, schedule);
                    }
                }}
                onDragStart={(e) => e.preventDefault()} // Prevent default HTML5 drag
            >
                <div style={{
                    padding: '4px 6px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: eventHeight > 30 ? 'flex-start' : 'center',
                    position: 'relative',
                    zIndex: 1,
                    fontFamily: 'Inter, sans-serif',
                    height: '100%',
                    color: eventColor,
                    background: bgColor,
                    boxShadow: `inset 0 0 0 1000px ${bgColor}`,
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        width: '100%',
                    }}>
                        <div style={{
                            flex: 1,
                            paddingRight: '4px',
                        }}>
                            <div style={{
                                fontSize: '13px',  // Increased from 11px
                                fontWeight: '600',
                                lineHeight: '1.3', // Slightly increased line height
                                marginBottom: timeDisplay && eventHeight > 30 ? '3px' : '0', // Increased spacing
                                color: eventColor,
                                background: bgColor,
                            }}>
                                {schedule.title || 'Work Hours'}
                            </div>
                            {timeDisplay && eventHeight > 30 && (
                                <div style={{
                                    fontSize: '11px',  // Increased from 9px
                                    fontWeight: '500', // Made slightly bolder
                                    opacity: 0.9,      // Made more visible
                                    lineHeight: '1.2', // Slightly increased line height
                                    color: eventColor,
                                    background: bgColor,
                                }}>
                                    {timeDisplay}
                                </div>
                            )}
                        </div>
                        {/* Event Icons */}
                        {(hasZoomMeeting(schedule) || hasParticipantFiles(schedule) || (schedule.participants && schedule.participants.length > 0) || schedule.isRecurringInstance) && (
                            <div style={{
                                display: 'flex',
                                gap: '2px',
                                marginTop: '2px',
                            }}>
                                {schedule.isRecurringInstance && (
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '12px',
                                        background: eventColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                    }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                            <path d="M12 4V1L8 5L12 9V6C15.31 6 18 8.69 18 12C18 13.01 17.75 13.97 17.3 14.8L18.76 16.26C19.54 15.03 20 13.57 20 12C20 7.58 16.42 4 12 4ZM12 18C8.69 18 6 15.31 6 12C6 10.99 6.25 10.03 6.7 9.2L5.24 7.74C4.46 8.97 4 10.43 4 12C4 16.42 7.58 20 12 20V23L16 19L12 15V18Z"/>
                                        </svg>
                                    </div>
                                )}
                                {hasZoomMeeting(schedule) && (
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '12px',
                                        background: eventColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                    }}>
                                        <svg width="10" height="6.5" viewBox="0 0 20 13" fill="rgba(255, 255, 255, 0.9)" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.75 0C2.02065 0 1.32118 0.289731 0.805456 0.805456C0.289731 1.32118 0 2.02065 0 2.75V10.25C0 10.9793 0.289731 11.6788 0.805456 12.1945C1.32118 12.7103 2.02065 13 2.75 13H11.25C11.9793 13 12.6788 12.7103 13.1945 12.1945C13.7103 11.6788 14 10.9793 14 10.25V8.656L17.419 11.701C18.224 12.418 19.5 11.846 19.5 10.767V1.865C19.5 0.785 18.224 0.214 17.419 0.931L14 3.976V2.75C14 2.02065 13.7103 1.32118 13.1945 0.805456C12.6788 0.289731 11.9793 0 11.25 0H2.75Z"/>
                                        </svg>
                                    </div>
                                )}
                                {hasParticipantFiles(schedule) && (
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '12px',
                                        background: eventColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                    }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"/>
                                        </svg>
                                    </div>
                                )}
                                {(schedule.participants && schedule.participants.length > 0) && (
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '12px',
                                        background: eventColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                    }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }, [viewMode, windowDimensions, teamMemberLimit, groupedEmployees, actualCellHeight, handleEventClick]);

    // Add function to toggle expanded state
    const toggleEmployeeExpanded = (employeeId, event) => {
        event.stopPropagation(); // Prevent any parent click handlers
        setExpandedEmployees(prev => {
            const newSet = new Set(prev);
            if (newSet.has(employeeId)) {
                newSet.delete(employeeId);
            } else {
                newSet.add(employeeId);
            }
            return newSet;
        });
    };

    // Update the expanded info panel
    const renderExpandedInfo = (employee) => {
        const isEditingThisRole = editingRole === employee.id;
        const currentRole = getEmployeeRole(employee); // Use the new function
        const roleColor = getRoleColor(currentRole);

        return (
            <div style={styles.expandedInfo}>
                <div style={styles.expandedInfoRow}>
                    <span style={styles.expandedInfoLabel}>Email:</span>
                    <span style={styles.expandedInfoValue}>{employee.email}</span>
                                    </div>
                <div style={styles.expandedInfoRow}>
                    <span style={styles.expandedInfoLabel}>Role:</span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '12px',
                                backgroundColor: roleColor,
                                marginRight: '8px',
                            }}></div>
                            <span style={styles.expandedInfoValue}>{currentRole}</span>
                                    </div>
                        <button
                            style={roleEditStyles.editRoleButton}
                            onClick={() => {
                                setEditingRole(employee.id);
                                setNewRole(currentRole);
                                setSelectedColor(roleColor);
                            }}
                            title="Edit role"
                        >
                            Edit
                        </button>
                                </div>
                            </div>

                {/* Role editing interface */}
                {isEditingThisRole && (
                    <div style={roleEditStyles.roleEditContainer}>
                        <input
                            type="text"
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                            placeholder="Enter role name"
                            style={roleEditStyles.roleInput}
                            autoFocus
                        />
                        
                        <div style={roleEditStyles.colorPicker}>
                            {roleColors.map(color => (
                                <div
                                    key={color}
                                    style={{
                                        ...roleEditStyles.colorOption,
                                        backgroundColor: color,
                                        ...(selectedColor === color ? roleEditStyles.colorOptionSelected : {})
                                    }}
                                    onClick={() => setSelectedColor(color)}
                                    title={`Select ${color}`}
                                />
                            ))}
                </div>

                        <div style={roleEditStyles.roleEditButtons}>
                            <button
                                            style={{
                                    ...roleEditStyles.roleEditButton,
                                    ...roleEditStyles.cancelButton
                                }}
                                onClick={() => {
                                    setEditingRole(null);
                                    setNewRole('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    ...roleEditStyles.roleEditButton,
                                    ...roleEditStyles.saveButton
                                }}
                                onClick={() => updateEmployeeRole(employee.id, newRole, selectedColor)}
                                disabled={!newRole.trim()}
                            >
                                Save
                            </button>
                                        </div>
                                </div>
                        )}

                {/* Manager Toggle */}
                <div style={styles.expandedInfoRow}>
                    <span style={styles.expandedInfoLabel}>Manager Status:</span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#555'
                        }}>
                            <input
                                type="checkbox"
                                checked={JSON.parse(localStorage.getItem('employeeManagerStatuses') || '{}')[employee.id] || false}
                                onChange={async (e) => {
                                    await updateEmployeeManagerStatus(employee.id, e.target.checked);
                                }}
                                style={{
                                    marginRight: '8px',
                                    transform: 'scale(1.2)',
                                    accentColor: '#4CAF50'
                                }}
                            />
                            <span style={{
                                fontWeight: JSON.parse(localStorage.getItem('employeeManagerStatuses') || '{}')[employee.id] ? '600' : 'normal',
                                color: JSON.parse(localStorage.getItem('employeeManagerStatuses') || '{}')[employee.id] ? '#4CAF50' : '#666'
                            }}>
                                {JSON.parse(localStorage.getItem('employeeManagerStatuses') || '{}')[employee.id] 
                                    ? `Manager of ${currentRole}` 
                                    : `Promote to Manager of ${currentRole}`
                                }
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        );
    };

    // Function to handle role-wide participant selection
    const handleRoleParticipantToggle = useCallback((role, employees) => {
        const roleEmployeeIds = employees.map(emp => emp.id);
        const allRoleSelected = roleEmployeeIds.every(id => 
            prefilledParticipants.some(p => p.id === id)
        );
        
        if (allRoleSelected) {
            // Remove all employees from this role
            setPrefilledParticipants(prev => 
                prev.filter(p => !roleEmployeeIds.includes(p.id))
            );
        } else {
            // Add all employees from this role
            const newParticipants = employees.filter(emp => 
                !prefilledParticipants.some(p => p.id === emp.id)
            );
            setPrefilledParticipants(prev => [...prev, ...newParticipants]);
        }
    }, [prefilledParticipants, setPrefilledParticipants]);

    // Update the role header to show role colors
    const renderRoleHeader = useCallback((role, count, employees) => {
        const roleColor = getRoleColor(role);
        const roleEmployeeIds = employees.map(emp => emp.id);
        const allRoleSelected = roleEmployeeIds.every(id => 
            prefilledParticipants.some(p => p.id === id)
        );
        const someRoleSelected = roleEmployeeIds.some(id => 
            prefilledParticipants.some(p => p.id === id)
        );
        
        return (
                                    <div style={{
                ...roleGroupStyles.roleHeader,
                backgroundColor: hexToRgba(roleColor, 0.05),
                color: hexToRgba(roleColor, 0.8)
                                    }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                                            style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: allRoleSelected ? '#666666' : (someRoleSelected ? '#999999' : 'transparent'),
                            border: `2px solid #666666`,
                            borderRadius: '3px',
                            marginRight: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0'
                        }}
                        onClick={() => handleRoleParticipantToggle(role, employees)}
                        title={allRoleSelected ? `Remove all ${role}s from participants` : `Add all ${role}s as participants`}
                    >
                        {allRoleSelected && (
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                        {someRoleSelected && !allRoleSelected && (
                            <div style={{
                                width: '6px',
                                height: '2px',
                                backgroundColor: 'white',
                                borderRadius: '1px'
                            }}></div>
                        )}
                    </button>
                    <span>{role}</span>
                    </div>
                <span style={{
                    ...roleGroupStyles.roleCount,
                    color: hexToRgba(roleColor, 0.6)
                }}>
                    {count}
                </span>
                </div>
        );
    }, [getRoleColor, hexToRgba, roleGroupStyles, prefilledParticipants, handleRoleParticipantToggle]);

    // Update the team column rendering to use the new functions
    const renderTeamColumn = () => {
        return (
                    <div style={styles.teamColumn} className="teamColumn">
                {groupedEmployees.map((group, groupIndex) => (
                    <React.Fragment key={group.role}>
                        {/* Role header with color */}
                        {renderRoleHeader(group.role, group.employees.length, group.employees)}
                        
                        {/* Employees in this role */}
                        {group.employees.map(employee => {
                            const monthlyHours = Math.round(calculateMonthlyHours(employee.id) * 100) / 100;
                            const isExpanded = expandedEmployees.has(employee.id);
                            
                            return (
                                <div 
                                    key={employee.id} 
                                    style={styles.employeeCell} 
                                    className="employeeCell"
                                    ref={groupIndex === 0 && group.employees.indexOf(employee) === 0 ? cellRef : null}
                                >
                                    {/* Participant selection box */}
                                    <div 
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            backgroundColor: prefilledParticipants.some(p => p.id === employee.id) ? '#2D2D2D' : 'transparent',
                                            border: '2px solid #2D2D2D',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            marginRight: '8px',
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onClick={(e) => handlePrefillParticipant(employee, e)}
                                        title={`${prefilledParticipants.some(p => p.id === employee.id) ? 'Remove' : 'Add'} ${employee.email.split('@')[0]} ${prefilledParticipants.some(p => p.id === employee.id) ? 'from' : 'to'} participants`}
                                    >
                                        {prefilledParticipants.some(p => p.id === employee.id) && (
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>
                                    {renderAvatar(employee)}
                                    <div style={styles.employeeInfo}>
                                        <div style={styles.employeeDetails}>
                                            <div style={styles.employeeName}>{employee.first_name} {employee.last_name}</div>
                                            <div style={styles.employeeRole}>{getEmployeeRole(employee)}</div>
                                        </div>
                                    </div>

                                    {/* Hours display in top right corner, next to 3 dots */}
                                    <div style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '36px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        zIndex: 3
                                    }}>
                                        <div className="hours-summary-badge">
                                          {monthlyHours}/200h
                                        </div>
                                    </div>

                                    {/* Add expand button */}
                                    <button
                                        style={{
                                            ...styles.expandButton,
                                            opacity: isExpanded ? 1 : 0.6,
                                        }}
                                        onClick={(e) => toggleEmployeeExpanded(employee.id, e)}
                                        title={isExpanded ? 'Show less' : 'Show more'}
                                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                                        onMouseLeave={(e) => e.target.style.opacity = isExpanded ? '1' : '0.6'}
                                    >
                                        <svg 
                                            width="12" 
                                            height="12" 
                                            viewBox="0 0 16 16" 
                                            fill="none" 
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="3" cy="8" r="1.5" fill="currentColor"/>
                                            <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                            <circle cx="13" cy="8" r="1.5" fill="currentColor"/>
                                        </svg>
                                    </button>

                                    {/* Expanded info panel with role editing */}
                                    {isExpanded && renderExpandedInfo(employee)}
                                </div>
                            );
                        })}
                        
                        {/* Add separator between role groups (except for the last group) */}
                        {groupIndex < groupedEmployees.length - 1 && (
                            <div style={roleGroupStyles.roleSeparator}></div>
                        )}
                    </React.Fragment>
                ))}
                    </div>
        );
    };

    // Update the schedule grid rendering to match the grouped structure
    const renderScheduleGrid = () => {
        return (
                            <div style={styles.scheduleGrid} className="scheduleGrid">
                {groupedEmployees.map((group, groupIndex) => (
                    <React.Fragment key={`schedule-${group.role}`}>
                        {/* Role header divider - separate from grid */}
                        <div style={{
                            width: '100%',
                            height: '24px',
                            backgroundColor: hexToRgba(getRoleColor(group.role), 0.08),
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '16px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: hexToRgba(getRoleColor(group.role), 0.8),
                            position: 'relative',
                            zIndex: 2,
                        }}>
                            {/* This is a true divider, not part of the time grid */}
                        </div>
                        
                        {/* Employee rows for this role group */}
                        {group.employees.map((employee, employeeIndex) => (
                                    <div key={employee.id} style={styles.timeRow} className="timeRow">
                                        {viewMode === 'week' ? (
                                            Array.from({ length: 7 }, (_, dayIndex) => {
                                                const date = calendarDate.clone().startOf('week').add(dayIndex, 'days');
                                                const daySchedules = schedules.filter(schedule => {
                                                    const scheduleStart = moment(schedule.start);
                                                    return schedule.resource === String(employee.id) && 
                                                           scheduleStart.isSame(date, 'day');
                                                });

                                                const isHighlighted = isSlotHighlighted(employee.id, 0, date.toDate());
                                                const isExpanded = isDayExpanded(employee.id, date.toDate());
                                                const visibleSchedules = isExpanded ? daySchedules : daySchedules.slice(0, MAX_VISIBLE_EVENTS);
                                                const hasMoreEvents = daySchedules.length > MAX_VISIBLE_EVENTS;
                                                const isLastInGroup = employeeIndex === group.employees.length - 1;
                                                const dropTargetStyles = getDropTargetStyles(employee.id, 0, date.toDate());

                                                return (
                                                    <div key={dayIndex} style={{
                                                        ...styles.timeSlot,
                                                        borderRight: dayIndex === 6 ? 'none' : '1px solid var(--border-color)',
                                                        ...(isHighlighted && {
                                                            backgroundColor: 'rgba(239, 68, 68, 0.3)',
                                                            boxShadow: 'inset 0 0 0 2px #ef4444',
                                                            boxSizing: 'border-box',
                                                            borderRadius: '2px',
                                                            margin: '1px',
                                                        }),
                                                        cursor: isDragging ? 'grabbing' : 'pointer',
                                                        transition: 'all 0.1s ease',
                                                        ...dropTargetStyles
                                                    }}>
                                                        {/* Add grid overlay */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            borderRight: dayIndex === 6 ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                                                            pointerEvents: 'none'
                                                        }} />
                                                        
                                                        {/* Render visible events */}
                                                        {visibleSchedules.map(schedule => 
                                                            renderEvent(schedule, employee, date.toDate(), moment(schedule.start).hour())
                                                        )}
                                                        
                                                        {/* Show more button */}
                                                        {hasMoreEvents && !isExpanded && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '4px',
                                                                    right: '4px',
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                                    color: 'white',
                                                                    borderRadius: '12px',
                                                                    padding: '2px 8px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '500',
                                                                    cursor: 'pointer',
                                                                    zIndex: 10,
                                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                                    transition: 'all 0.2s ease',
                                                                    userSelect: 'none'
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleDayExpansion(employee.id, date.toDate());
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                                                                    e.target.style.transform = 'scale(1.05)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                                                    e.target.style.transform = 'scale(1)';
                                                                }}
                                                            >
                                                                +{daySchedules.length - MAX_VISIBLE_EVENTS}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Show less button when expanded */}
                                                        {hasMoreEvents && isExpanded && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '4px',
                                                                    right: '4px',
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                                    color: 'white',
                                                                    borderRadius: '12px',
                                                                    padding: '2px 8px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '500',
                                                                    cursor: 'pointer',
                                                                    zIndex: 10,
                                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                                    transition: 'all 0.2s ease',
                                                                    userSelect: 'none'
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleDayExpansion(employee.id, date.toDate());
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                                                                    e.target.style.transform = 'scale(1.05)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                                                    e.target.style.transform = 'scale(1)';
                                                                }}
                                                            >
                                                                Show Less
                                                            </div>
                                                        )}
                                                        
                                                        <div
                                                            style={{
                                                                cursor: isDragging ? 'grabbing' : 'pointer',
                                                                backgroundColor: 'transparent',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                zIndex: 0,
                                                                border: 'none',
                                                                userSelect: 'none'
                                                            }}
                                                            data-employee-id={employee.id}
                                                            data-hour={0}
                                                            data-date={date.format('YYYY-MM-DD')}
                                                            onMouseDown={(e) => handleTimeSlotMouseDown(e, employee, date.toDate(), 0)}
                                                            onMouseMove={(e) => handleTimeSlotMouseMove(e, employee, date.toDate(), 0)}
                                                            onClick={(e) => !isDragging && handleTimeSlotClick(e, employee, date.toDate(), 0)}
                                                        />
                                                        
                                                        {/* Add hour divider grid lines */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            borderRight: '1px solid var(--border-color)',
                                                            borderBottom: '1px solid var(--border-color)',
                                                            pointerEvents: 'none',
                                                            opacity: 0.5
                                                        }} />
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            hours.map(hour => {
                                                const isHighlighted = isSlotHighlighted(employee.id, hour, calendarDate.toDate());
                                                const dropTargetStyles = getDropTargetStyles(employee.id, hour, calendarDate.toDate());
                                                
                                                return (
                                                    <div key={hour} style={{
                                                        ...styles.timeSlot,
                                                        ...(isHighlighted && {
                                                            backgroundColor: 'rgba(239, 68, 68, 0.3)',
                                                            boxShadow: 'inset 0 0 0 2px #ef4444',
                                                            boxSizing: 'border-box',
                                                            borderRadius: '2px',
                                                            margin: '1px',
                                                        }),
                                                        cursor: isDragging ? 'grabbing' : 'pointer',
                                                        transition: 'all 0.1s ease',
                                                        ...dropTargetStyles
                                                    }}>
                                                        {getSchedulesForEmployeeAndHour(employee.id, hour, calendarDate.toDate()).map(schedule => 
                                                            renderEvent(schedule, employee, calendarDate.toDate(), hour)
                                                        )}
                                                        <div
                                                            style={{
                                                                cursor: isDragging ? 'grabbing' : 'pointer',
                                                                backgroundColor: 'transparent',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                zIndex: 0,
                                                                border: 'none',
                                                                userSelect: 'none'
                                                            }}
                                                            data-employee-id={employee.id}
                                                            data-hour={hour}
                                                            data-date={calendarDate.format('YYYY-MM-DD')}
                                                            onMouseDown={(e) => handleTimeSlotMouseDown(e, employee, calendarDate.toDate(), hour)}
                                                            onMouseMove={(e) => handleTimeSlotMouseMove(e, employee, calendarDate.toDate(), hour)}
                                                            onMouseEnter={(e) => handleTimeSlotHover(e, employee, calendarDate.toDate(), hour)}
                                                            onMouseLeave={handleTimeSlotMouseLeave}
                                                            onClick={(e) => !isDragging && handleTimeSlotClick(e, employee, calendarDate.toDate(), hour)}
                                                        />
                                                        
                                                        {/* 30-minute segment highlights */}
                                                        {isFirstHalfHovered(employee.id, hour, calendarDate.toDate()) && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                height: '50%',
                                                                backgroundColor: 'rgba(59, 130, 246, 0.15)',
                                                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                                                borderRadius: '2px',
                                                                pointerEvents: 'none',
                                                                zIndex: 1
                                                            }} />
                                                        )}
                                                        
                                                        {isSecondHalfHovered(employee.id, hour, calendarDate.toDate()) && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                bottom: 0,
                                                                left: 0,
                                                                right: 0,
                                                                height: '50%',
                                                                backgroundColor: 'rgba(59, 130, 246, 0.15)',
                                                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                                                borderRadius: '2px',
                                                                pointerEvents: 'none',
                                                                zIndex: 1
                                                            }} />
                                                        )}
                                                        
                                                        {/* Add visual indicators for 30-minute marks */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: 0,
                                                            right: 0,
                                                            height: '1px',
                                                            backgroundColor: 'var(--border-color)',
                                                            opacity: 0.3,
                                                            pointerEvents: 'none'
                                                        }} />
                                                        
                                                        {/* Add hour divider grid lines */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            borderRight: '1px solid var(--border-color)',
                                                            borderBottom: '1px solid var(--border-color)',
                                                            pointerEvents: 'none',
                                                            opacity: 0.5
                                                        }} />
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                ))}
                        
                        {/* Add separator between role groups in schedule grid */}
                        {groupIndex < groupedEmployees.length - 1 && (
                            <div style={{
                                width: '100%',
                                height: '4px',
                                backgroundColor: 'var(--bg-primary)',
                            }}>
                                {/* This is also a true separator, not grid cells */}
                            </div>
                        )}
                    </React.Fragment>
                ))}
                        </div>
        );
    };

    // Team member selector component
    const renderTeamMemberSelector = () => {
        // Generate dynamic options based on actual employee count
        const totalEmployees = employees.length;
        const generateOptions = () => {
            // Always provide common viewing options regardless of total employee count
            const commonOptions = [1, 2, 3, 4, 5, 6, 8, 10];
            
            // Add larger increments for bigger teams
            const largerOptions = [15, 20, 25, 30, 40, 50];
            
            // Combine all options that are less than or equal to total employees
            const availableOptions = [...commonOptions, ...largerOptions]
                .filter(option => option <= totalEmployees)
                .sort((a, b) => a - b);
            
            // Always include the total employee count as "All" option
            if (!availableOptions.includes(totalEmployees)) {
                availableOptions.push(totalEmployees);
            }
            
            // Remove duplicates and ensure unique values
            return [...new Set(availableOptions)].sort((a, b) => a - b);
        };
        
        const options = generateOptions();
        const totalPages = Math.ceil(totalEmployees / teamMemberLimit);
        const startEmployee = (currentPage - 1) * teamMemberLimit + 1;
        const endEmployee = Math.min(currentPage * teamMemberLimit, totalEmployees);
        
        const handlePreviousPage = () => {
            if (currentPage > 1) {
                setCurrentPage(currentPage - 1);
            }
        };
        
        const handleNextPage = () => {
            if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1);
            }
        };
        
        const handlePageChange = (page) => {
            setCurrentPage(page);
        };
        
        const handleLimitChange = (newLimit) => {
            setTeamMemberLimit(parseInt(newLimit));
            // Reset to page 1 when changing limit
            setCurrentPage(1);
        };
        
        return (
            <div style={{
                position: 'fixed',
                bottom: '16px',
                right: '16px',
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '8px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                fontSize: '12px',
                color: 'var(--text-primary)',
                maxWidth: '280px'
            }}>
                {/* Members per page selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <select
                        value={teamMemberLimit}
                        onChange={(e) => handleLimitChange(e.target.value)}
                        style={{
                            backgroundColor: 'var(--bg-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '11px',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            outline: 'none',
                            minWidth: '45px'
                        }}
                    >
                        {options.map(option => (
                            <option key={option} value={option}>
                                {option === totalEmployees ? `All` : option}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Pagination controls - only show if there are multiple pages */}
                {totalPages > 1 && (
                    <>
                        <div style={{width: '1px', height: '14px', backgroundColor: 'var(--border-color)'}} />
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {/* Previous button */}
                            <button
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1}
                                style={{
                                    backgroundColor: 'var(--bg-primary)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '3px',
                                    padding: '2px 6px',
                                    fontSize: '11px',
                                    color: currentPage === 1 ? 'var(--text-secondary)' : 'var(--text-primary)',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    outline: 'none',
                                    opacity: currentPage === 1 ? 0.5 : 1
                                }}
                            >
                                ‹
                            </button>

                            {/* Simplified page indicator */}
                            <span style={{ 
                                fontSize: '11px', 
                                color: 'var(--text-secondary)',
                                minWidth: '20px',
                                textAlign: 'center'
                            }}>
                                {currentPage}/{totalPages}
                            </span>

                            {/* Next button */}
                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                                style={{
                                    backgroundColor: 'var(--bg-primary)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '3px',
                                    padding: '2px 6px',
                                    fontSize: '11px',
                                    color: currentPage === totalPages ? 'var(--text-secondary)' : 'var(--text-primary)',
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    outline: 'none',
                                    opacity: currentPage === totalPages ? 0.5 : 1
                                }}
                            >
                                ›
                            </button>
                        </div>
                    </>
                )}

                {/* Simplified members info */}
                <div style={{width: '1px', height: '14px', backgroundColor: 'var(--border-color)'}} />
                <span style={{ 
                    fontSize: '11px', 
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap'
                }}>
                    {startEmployee}-{endEmployee} of {totalEmployees}
                </span>
            </div>
        );
    };

    // Update CSS variable for member count and handle window resize
    React.useEffect(() => {
        const updateCSSVariable = () => {
            const root = document.documentElement;
            // Calculate actual displayed employee count
            const displayedEmployeeCount = groupedEmployees.reduce((total, group) => total + group.employees.length, 0);
            root.style.setProperty('--visible-members', displayedEmployeeCount);
        };

        updateCSSVariable();

        // Debounced resize handler to prevent excessive calls
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                updateCSSVariable();
                
                // Update team member limit based on screen height
                const height = window.innerHeight;
                let newLimit;
                if (height <= 800) {
                    newLimit = 3;
                } else if (height <= 1000) {
                    newLimit = 4;
                } else {
                    newLimit = 5;
                }
                
                if (newLimit !== teamMemberLimit) {
                    console.log(`🖥️ Screen height changed: ${height}px, adjusting team limit to ${newLimit}`);
                    setTeamMemberLimit(newLimit);
                    setCurrentPage(1); // Reset to first page when limit changes
                }
                
                // Update window dimensions to trigger event recalculation
                setWindowDimensions({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }, 150); // Debounce by 150ms
        };

        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, [groupedEmployees, teamMemberLimit]);

    // Add function to toggle day expansion
    const toggleDayExpansion = (employeeId, date) => {
        const dayKey = `${employeeId}-${moment(date).format('YYYY-MM-DD')}`;
        const newExpandedDays = new Set(expandedDays);
        
        if (newExpandedDays.has(dayKey)) {
            newExpandedDays.delete(dayKey);
        } else {
            newExpandedDays.add(dayKey);
        }
        
        setExpandedDays(newExpandedDays);
    };

    // Check if a day is expanded
    const isDayExpanded = (employeeId, date) => {
        const dayKey = `${employeeId}-${moment(date).format('YYYY-MM-DD')}`;
        return expandedDays.has(dayKey);
    };

    // Update teamMemberLimit when employees are first loaded - DISABLED for pagination
    React.useEffect(() => {
        // Disabled to allow pagination functionality
        // if (employees.length > 0 && teamMemberLimit === 5 && employees.length !== 5) {
        //     // Only update if we're still at the default value and employee count is different
        //     setTeamMemberLimit(employees.length);
        // }
    }, [employees.length, teamMemberLimit]);

    // Force re-render when teamMemberLimit changes to ensure events recalculate
    React.useEffect(() => {
        // Force window dimensions update to trigger event recalculation
        setWindowDimensions({
            width: window.innerWidth,
            height: window.innerHeight
        });
    }, [teamMemberLimit]);

    // Measure actual cell height from DOM
    React.useEffect(() => {
        const measureCellHeight = () => {
            if (cellRef.current) {
                const height = cellRef.current.getBoundingClientRect().height;
                if (height > 0 && height !== actualCellHeight) {
                    setActualCellHeight(height);
                }
            }
        };

        measureCellHeight();
        
        // Re-measure after a short delay to ensure layout is complete
        const timer = setTimeout(measureCellHeight, 100);
        
        return () => clearTimeout(timer);
    }, [teamMemberLimit, groupedEmployees, windowDimensions]);

    // Event drag handlers for moving existing events
    const handleEventDragStart = (event, scheduleEvent) => {
        event.stopPropagation();
        console.log('🎯 Starting to drag event:', scheduleEvent);
        
        const rect = event.currentTarget.getBoundingClientRect();
        setDragOffset({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        });
        
        // Store in both state and ref for immediate access
        setDraggingEvent(scheduleEvent);
        draggingEventRef.current = scheduleEvent;
        
        setDragPreview({
            ...scheduleEvent,
            x: event.clientX,
            y: event.clientY
        });
        
        // Prevent event preview from showing during drag
        setEventPreview(null);
        
        // Add global mouse move and up listeners
        document.addEventListener('mousemove', handleEventDragMove);
        document.addEventListener('mouseup', handleEventDragEnd);
    };

    const dragAnimationRef = useRef(null);

    const handleEventDragMove = (event) => {
        if (!draggingEventRef.current) return;
        
        // Use requestAnimationFrame to throttle updates for smooth performance
        if (dragAnimationRef.current) {
            cancelAnimationFrame(dragAnimationRef.current);
        }
        
        dragAnimationRef.current = requestAnimationFrame(() => {
            // Update drag preview position
            setDragPreview(prev => ({
                ...prev,
                x: event.clientX,
                y: event.clientY
            }));
            
            // Find the element under the cursor (throttled)
            const elementUnderCursor = document.elementFromPoint(event.clientX, event.clientY);
            if (elementUnderCursor) {
                const timeSlot = elementUnderCursor.closest('[data-employee-id][data-hour][data-date]');
                if (timeSlot) {
                    const employeeId = parseInt(timeSlot.getAttribute('data-employee-id'));
                    const hour = parseInt(timeSlot.getAttribute('data-hour'));
                    const date = timeSlot.getAttribute('data-date');
                    
                    const newDropTarget = {
                        employeeId,
                        hour,
                        date: moment(date).toDate()
                    };
                    
                    // Only update if the drop target actually changed
                    const currentDropTarget = dropTargetRef.current;
                    const hasChanged = !currentDropTarget || 
                        currentDropTarget.employeeId !== employeeId ||
                        currentDropTarget.hour !== hour ||
                        moment(currentDropTarget.date).format('YYYY-MM-DD') !== moment(newDropTarget.date).format('YYYY-MM-DD');
                    
                    if (hasChanged) {
                        setDropTarget(newDropTarget);
                        dropTargetRef.current = newDropTarget;
                    }
                } else {
                    // Only clear if we currently have a drop target
                    if (dropTargetRef.current) {
                        setDropTarget(null);
                        dropTargetRef.current = null;
                    }
                }
            } else {
                // Only clear if we currently have a drop target
                if (dropTargetRef.current) {
                    setDropTarget(null);
                    dropTargetRef.current = null;
                }
            }
        });
    };

    const handleEventDragEnd = async (event) => {
        const currentDraggingEvent = draggingEventRef.current;
        const currentDropTarget = dropTargetRef.current;
        
        // Clean up animation frame
        if (dragAnimationRef.current) {
            cancelAnimationFrame(dragAnimationRef.current);
            dragAnimationRef.current = null;
        }
        
        // Remove global listeners
        document.removeEventListener('mousemove', handleEventDragMove);
        document.removeEventListener('mouseup', handleEventDragEnd);
        
        if (currentDraggingEvent && currentDropTarget) {
            try {
                // Handle all-day events differently
                if (currentDraggingEvent.isAllDay) {
                    // For all-day events, just change the date and employee
                    const newStart = moment(currentDropTarget.date).startOf('day').toDate();
                    const newEnd = moment(currentDropTarget.date).endOf('day').toDate();
                    
                    const updatedEvent = {
                        ...currentDraggingEvent,
                        start: newStart,
                        end: newEnd,
                        resource: String(currentDropTarget.employeeId)
                    };
                    
                    // Update the existing event
                    await updateScheduleOnServer(updatedEvent);
                    
                    // Update local schedules
                    const updatedSchedules = schedules.map(schedule => 
                        schedule.id === currentDraggingEvent.id ? updatedEvent : schedule
                    );
                    setSchedules(updatedSchedules);
                    
                } else {
                    // Handle regular timed events
                    const originalStart = moment(currentDraggingEvent.start);
                    const originalEnd = moment(currentDraggingEvent.end);
                    const duration = moment.duration(originalEnd.diff(originalStart));
                    
                    // Create new start time based on drop target
                    const newStart = moment(currentDropTarget.date)
                        .hour(currentDropTarget.hour)
                        .minute(originalStart.minute())
                        .second(0)
                        .millisecond(0);
                    
                    // Calculate new end time maintaining the same duration
                    const newEnd = moment(newStart).add(duration);
                    
                    // Update the event with new times and employee
                    const updatedEvent = {
                        ...currentDraggingEvent,
                        start: newStart.toDate(),
                        end: newEnd.toDate(),
                        resource: String(currentDropTarget.employeeId)
                    };
                    
                    // Update the existing event
                    await updateScheduleOnServer(updatedEvent);
                    
                    // Update local schedules
                    const updatedSchedules = schedules.map(schedule => 
                        schedule.id === currentDraggingEvent.id ? updatedEvent : schedule
                    );
                    setSchedules(updatedSchedules);
                }
                
                // Refresh schedules from server
                if (refreshSchedules) {
                    refreshSchedules();
                }
                
            } catch (error) {
                console.error('❌ Error moving event:', error);
            }
        }
        
        // Clear drag state
        setDraggingEvent(null);
        draggingEventRef.current = null;
        setDragPreview(null);
        setDropTarget(null);
        dropTargetRef.current = null;
        setDragOffset({ x: 0, y: 0 });
    };

    // Render drag preview
    const renderDragPreview = () => {
        if (!dragPreview) return null;

        const employee = employees.find(emp => emp.id === parseInt(dragPreview.resource));
        const eventColor = employee?.color || '#4CAF50';

        return (
            <div
                style={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    transform: `translate(${dragPreview.x - dragOffset.x}px, ${dragPreview.y - dragOffset.y}px)`,
                    width: '200px',
                    height: '40px',
                    background: `${eventColor}15`,
                    border: `2px solid ${eventColor}`,
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: eventColor,
                    zIndex: 9999,
                    pointerEvents: 'none',
                    opacity: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    willChange: 'transform',
                }}
            >
                {dragPreview.title || 'Work Hours'}
            </div>
        );
    };

    // Add drop target highlighting styles - memoized for performance
    const getDropTargetStyles = useMemo(() => {
        return (employeeId, hour, date) => {
            if (!dropTarget) return {};
            
            const targetDate = moment(date).format('YYYY-MM-DD');
            const dropTargetDate = moment(dropTarget.date).format('YYYY-MM-DD');
            
            if (dropTarget.employeeId === employeeId && 
                dropTarget.hour === hour && 
                dropTargetDate === targetDate) {
                return {
                    backgroundColor: 'rgba(66, 99, 235, 0.3)',
                    border: '2px solid #4263eb',
                    transform: 'scale(1.02)',
                    transition: 'all 0.2s ease',
                };
            }
            
            return {};
        };
    }, [dropTarget]);

    // Reset to page 1 when employees change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [employees.length]);

    // Force re-render when teamMemberLimit changes to ensure events recalculate
    React.useEffect(() => {
        // Force window dimensions update to trigger event recalculation
        setWindowDimensions({
            width: window.innerWidth,
            height: window.innerHeight
        });
    }, [teamMemberLimit]);

    // Add dynamic height calculation for proper bottom alignment
    React.useEffect(() => {
        const calculateAvailableHeight = () => {
            const windowHeight = window.innerHeight;
            const headerSpace = 180; // Space for fixed headers/controls
            const roleCount = groupedEmployees.reduce((acc, group) => acc + 1, 0);
            const roleHeaderSpace = roleCount * 40; // 40px per role header
            const separatorSpace = Math.max(0, roleCount - 1) * 4; // 4px per separator
            const bufferSpace = 120; // Increased buffer to ensure 5th employee is fully visible
            
            const totalReservedSpace = headerSpace + roleHeaderSpace + separatorSpace + bufferSpace;
            const availableHeight = Math.max(300, windowHeight - totalReservedSpace); // Reduced minimum from 400 to 300
            
            console.log('📏 Height calculation details:', {
                windowHeight,
                headerSpace,
                roleHeaderSpace,
                separatorSpace,
                bufferSpace,
                totalReservedSpace,
                availableHeight
            });
            
            // Update CSS variables
            document.documentElement.style.setProperty('--available-height', `${availableHeight}px`);
            document.documentElement.style.setProperty('--visible-members', groupedEmployees.reduce((total, group) => total + group.employees.length, 0));
        };

        calculateAvailableHeight();
        
        // Recalculate on window resize
        const handleResize = () => {
            calculateAvailableHeight();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [groupedEmployees]);

    return (
        <div style={{
            ...styles.container,
            // Add any dynamic styles here
        }}>
            <div style={styles.mainContent}>
                <div style={{ position: 'relative', height: '16px' }}>
                    <div 
                        className="topScrollContainer" 
                        style={styles.topScrollContainer} 
                        onScroll={(e) => {
                            const scheduleGrid = document.querySelector('.scheduleGrid');
                            const timeHeader = document.querySelector('.timeHeader');
                            if (scheduleGrid) {
                                scheduleGrid.style.transform = `translateX(-${e.target.scrollLeft}px)`;
                            }
                            if (timeHeader) {
                                timeHeader.style.transform = `translateX(-${e.target.scrollLeft}px)`;
                            }
                        }}
                    >
                        <div style={styles.scrollContent}></div>
                    </div>
                </div>

                <div style={styles.summaryBarContainer}>
                    <div style={styles.summaryBarSpace}></div>
                    <div style={styles.summaryBar}>
                        {/* Settings Button - Far Left */}
                        <div style={{
                            marginRight: '2px',
                            marginLeft: '-50px',
                            marginTop: '25px',
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative'
                        }}>
                            <button
                                onClick={() => setShowProgressSettings(!showProgressSettings)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#666',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}
                                title="Progress Bar Settings"
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6.71729 4.5045C6.05424 4.5045 5.41836 4.76789 4.94952 5.23673C4.48068 5.70557 4.21729 6.34146 4.21729 7.0045C4.21729 7.66754 4.48068 8.30343 4.94952 8.77227C5.41836 9.24111 6.05424 9.5045 6.71729 9.5045C7.38033 9.5045 8.01621 9.24111 8.48505 8.77227C8.95389 8.30343 9.21729 7.66754 9.21729 7.0045C9.21729 6.34146 8.95389 5.70557 8.48505 5.23673C8.01621 4.76789 7.38033 4.5045 6.71729 4.5045ZM5.21729 7.0045C5.21729 6.60668 5.37532 6.22514 5.65662 5.94384C5.93793 5.66253 6.31946 5.5045 6.71729 5.5045C7.11511 5.5045 7.49664 5.66253 7.77795 5.94384C8.05925 6.22514 8.21729 6.60668 8.21729 7.0045C8.21729 7.40232 8.05925 7.78386 7.77795 8.06516C7.49664 8.34647 7.11511 8.5045 6.71729 8.5045C6.31946 8.5045 5.93793 8.34647 5.65662 8.06516C5.37532 7.78386 5.21729 7.40232 5.21729 7.0045ZM5.13729 13.8185C5.65479 13.942 6.18529 14.0045 6.71729 14.0045C7.24881 14.0039 7.77842 13.941 8.29529 13.817C8.44374 13.7819 8.57771 13.7017 8.67885 13.5875C8.77999 13.4733 8.84336 13.3306 8.86028 13.179L8.97729 12.114C8.98846 11.9978 9.02717 11.8859 9.09021 11.7876C9.15324 11.6893 9.23878 11.6074 9.33975 11.5488C9.44072 11.4902 9.5542 11.4564 9.67081 11.4504C9.78742 11.4443 9.90379 11.4661 10.0103 11.514L10.9878 11.942C11.1277 12.0037 11.2834 12.0203 11.4331 11.9894C11.5829 11.9585 11.7193 11.8816 11.8233 11.7695C12.5455 10.9912 13.0846 10.0614 13.4013 9.048C13.4465 8.9014 13.4451 8.74437 13.3972 8.59863C13.3492 8.45288 13.2572 8.32564 13.1338 8.2345L12.2693 7.597C12.1748 7.52826 12.0979 7.4381 12.0449 7.33392C11.9919 7.22974 11.9644 7.11449 11.9645 6.99762C11.9647 6.88074 11.9925 6.76557 12.0457 6.66152C12.099 6.55747 12.1761 6.46751 12.2708 6.399L13.1308 5.764C13.2553 5.67339 13.3481 5.54598 13.3963 5.39975C13.4445 5.25352 13.4455 5.09585 13.3993 4.949C13.0833 3.9352 12.5439 3.00521 11.8208 2.2275C11.7162 2.11572 11.5796 2.03911 11.4297 2.00821C11.2798 1.97732 11.124 1.99367 10.9838 2.055L10.0108 2.483C9.90414 2.52949 9.78805 2.55023 9.6719 2.54355C9.55576 2.53687 9.44281 2.50295 9.3422 2.44454C9.24159 2.38613 9.15613 2.30486 9.09274 2.20731C9.02934 2.10976 8.98979 1.99866 8.97729 1.883L8.85929 0.823C8.8425 0.669631 8.77819 0.525346 8.67536 0.410324C8.57253 0.295303 8.43632 0.215298 8.28578 0.1815C7.77243 0.0663284 7.24838 0.00549342 6.72229 0C6.19311 0.00628375 5.66599 0.0671053 5.14929 0.1815C4.99863 0.214519 4.86218 0.294091 4.75926 0.408952C4.65633 0.523813 4.59214 0.668141 4.57579 0.8215L4.45728 1.8825C4.44434 1.99834 4.40445 2.10954 4.34081 2.20719C4.27716 2.30484 4.19154 2.38623 4.09079 2.44485C3.99004 2.50346 3.87696 2.53767 3.76061 2.54473C3.64427 2.55179 3.52788 2.5315 3.42079 2.4855L2.44779 2.057C2.30743 1.99715 2.15209 1.98184 2.00276 2.01314C1.85342 2.04444 1.7173 2.12083 1.61279 2.232C0.889671 3.0105 0.349828 3.94088 0.0327854 4.955C-0.0121769 5.10151 -0.0105421 5.25837 0.037464 5.40391C0.0854701 5.54945 0.177479 5.67649 0.300785 5.7675L1.16329 6.4035C1.25724 6.47264 1.33362 6.56289 1.38627 6.66699C1.43891 6.77108 1.46634 6.8861 1.46634 7.00275C1.46634 7.1194 1.43891 7.23442 1.38627 7.33851C1.33362 7.44261 1.25724 7.53286 1.16329 7.602L0.300785 8.2395C0.177072 8.33074 0.0848409 8.45824 0.0369034 8.60429C-0.011034 8.75034 -0.0122986 8.9077 0.0332853 9.0545C0.350057 10.0693 0.889915 11.0004 1.61329 11.7795C1.68209 11.8534 1.76547 11.9122 1.85816 11.9522C1.95085 11.9922 2.05083 12.0126 2.15179 12.012C2.25329 12.012 2.35379 11.991 2.44729 11.9505L3.42479 11.521C3.5314 11.4749 3.64734 11.4545 3.76328 11.4614C3.87922 11.4683 3.99192 11.5023 4.09234 11.5606C4.19276 11.619 4.27809 11.7001 4.34147 11.7974C4.40485 11.8947 4.44452 12.0056 4.45728 12.121L4.57479 13.184C4.59213 13.3347 4.65539 13.4765 4.75601 13.5901C4.85663 13.7037 4.98973 13.7831 5.13729 13.8185ZM7.88778 12.886C7.11637 13.0448 6.3207 13.0448 5.54928 12.886L5.45179 12.016C5.40412 11.5904 5.20118 11.1973 4.88179 10.912C4.63184 10.6916 4.32461 10.5465 3.99563 10.4934C3.66666 10.4403 3.32937 10.4814 3.02279 10.612L2.21979 10.962C1.7014 10.3691 1.30273 9.68146 1.04579 8.937L1.75779 8.411C1.97776 8.24891 2.15656 8.0374 2.27979 7.79353C2.40302 7.54966 2.46722 7.28024 2.46722 7.007C2.46722 6.73376 2.40302 6.46434 2.27979 6.22047C2.15656 5.9766 1.97776 5.76509 1.75779 5.603L1.04729 5.078C1.30446 4.33425 1.70312 3.64729 2.22129 3.055L3.02129 3.405C3.24179 3.50237 3.48024 3.55245 3.72129 3.552C4.15053 3.55065 4.56431 3.39159 4.88393 3.10507C5.20355 2.81855 5.40671 2.42455 5.45479 1.998L5.55178 1.1245C5.93878 1.0495 6.33179 1.0095 6.72529 1.0045C7.11629 1.0095 7.50629 1.0495 7.89029 1.1245L7.98328 1.9945C8.0296 2.42132 8.23208 2.81595 8.55179 3.1025C8.80215 3.32377 9.11018 3.46942 9.44006 3.52252C9.76995 3.57563 10.1081 3.53401 10.4153 3.4025L11.2153 3.0525C11.7339 3.64486 12.1329 4.332 12.3903 5.076L11.6788 5.599C11.4577 5.76053 11.2778 5.97196 11.1538 6.21609C11.0298 6.46023 10.9652 6.73018 10.9652 7.004C10.9652 7.27782 11.0298 7.54777 11.1538 7.79191C11.2778 8.03604 11.4577 8.24747 11.6788 8.409L12.3898 8.934C12.1317 9.6764 11.7337 10.3624 11.2173 10.955L10.4173 10.605C10.1669 10.4936 9.89379 10.443 9.62015 10.4574C9.34652 10.4718 9.08017 10.5507 8.84288 10.6877C8.60559 10.8248 8.4041 11.016 8.25487 11.2458C8.10565 11.4756 8.01293 11.7375 7.98429 12.01L7.88778 12.886Z" fill="currentColor"/>
                                </svg>
                            </button>
                            
                            {/* Settings Dropdown */}
                            {showProgressSettings && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    background: '#1E1E1E',
                                    border: '1px solid #666',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    zIndex: 1000,
                                    minWidth: '200px'
                                }}>
                                    <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#333'}}>
                                        Progress Bar Settings
                                    </div>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                        <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                            <input
                                                type="checkbox"
                                                checked={progressBarSettings.showWeekly}
                                                onChange={() => handleProgressBarToggle('showWeekly')}
                                                style={{cursor: 'pointer'}}
                                            />
                                            <span style={{fontSize: '13px', color: '#555'}}>Weekly Hours</span>
                                        </label>
                                        <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                            <input
                                                type="checkbox"
                                                checked={progressBarSettings.showMonthly}
                                                onChange={() => handleProgressBarToggle('showMonthly')}
                                                style={{cursor: 'pointer'}}
                                            />
                                            <span style={{fontSize: '13px', color: '#555'}}>Monthly Hours</span>
                                        </label>
                                        <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                            <input
                                                type="checkbox"
                                                checked={progressBarSettings.showDaily}
                                                onChange={() => handleProgressBarToggle('showDaily')}
                                                style={{cursor: 'pointer'}}
                                            />
                                            <span style={{fontSize: '13px', color: '#555'}}>Daily Hours</span>
                                        </label>
                                        <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                            <input
                                                type="checkbox"
                                                checked={progressBarSettings.showTTM}
                                                onChange={() => handleProgressBarToggle('showTTM')}
                                                style={{cursor: 'pointer'}}
                                            />
                                            <span style={{fontSize: '13px', color: '#555'}}>TTM (12 Months)</span>
                                        </label>
                                        <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                            <input
                                                type="checkbox"
                                                checked={progressBarSettings.showYearly}
                                                onChange={() => handleProgressBarToggle('showYearly')}
                                                style={{cursor: 'pointer'}}
                                            />
                                            <span style={{fontSize: '13px', color: '#555'}}>Yearly Hours</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {progressBarSettings.showWeekly && (
                            <div style={{...styles.summaryItem, flex: 'none', width: '300px'}}>
                                <span style={styles.summaryLabel}>Total working hours per week</span>
                                <div style={{...styles.summaryValueWithProgress, flexDirection: 'row', alignItems: 'center', gap: '16px'}}>
                                    <div style={{flex: 1}}>
                                        <div style={{...styles.summaryProgress}}>
                                            <div style={{
                                                ...styles.progressBar,
                                                width: `${weeklyProgressWidth}%`,
                                                backgroundColor: getProgressBarColor(weeklyProgressWidth)
                                            }} />
                                        </div>
                                    </div>
                                    <span style={{...styles.summaryValue, marginLeft: '8px'}}>{totalHours}/{weeklyTarget}h</span>
                                </div>
                            </div>
                        )}
                        
                        {progressBarSettings.showWeekly && progressBarSettings.showMonthly && (
                            <div style={{width: '1px', height: '48px', backgroundColor: '#555', margin: '8px 24px 0'}} />
                        )}
                        
                        {progressBarSettings.showMonthly && (
                            <div style={{...styles.summaryItem, flex: 2}}>
                                <span style={styles.summaryLabel}>Total for {moment().format('MMMM')}</span>
                                <div style={{...styles.summaryValueWithProgress, flexDirection: 'row', alignItems: 'center', gap: '16px'}}>
                                    <div style={{flex: 1}}>
                                        <div style={styles.summaryProgress}>
                                            <div style={{
                                                ...styles.progressBar,
                                                width: `${monthlyProgressWidth}%`,
                                                backgroundColor: getProgressBarColor(monthlyProgressWidth)
                                            }} />
                                        </div>
                                    </div>
                                    <span style={{...styles.summaryValue, marginLeft: '8px'}}>{totalHours}/{totalTarget}h</span>
                                </div>
                            </div>
                        )}
                        
                        {/* Additional Progress Bars */}
                        {((progressBarSettings.showWeekly || progressBarSettings.showMonthly) && progressBarSettings.showDaily) && (
                            <div style={{width: '1px', height: '48px', backgroundColor: '#555', margin: '8px 24px 0'}} />
                        )}
                        
                        {progressBarSettings.showDaily && (
                            <div style={{...styles.summaryItem, flex: 'none', width: '200px'}}>
                                <span style={styles.summaryLabel}>Today</span>
                                <div style={{...styles.summaryValueWithProgress, flexDirection: 'row', alignItems: 'center', gap: '16px'}}>
                                    <div style={{flex: 1}}>
                                        <div style={styles.summaryProgress}>
                                            <div style={{
                                                ...styles.progressBar,
                                                width: `${dailyProgressWidth}%`,
                                                backgroundColor: getProgressBarColor(dailyProgressWidth)
                                            }} />
                                        </div>
                                    </div>
                                    <span style={{...styles.summaryValue, marginLeft: '8px'}}>{dailyHours}/{dailyTarget}h</span>
                                </div>
                            </div>
                        )}
                        
                        {((progressBarSettings.showWeekly || progressBarSettings.showMonthly || progressBarSettings.showDaily) && progressBarSettings.showTTM) && (
                            <div style={{width: '1px', height: '48px', backgroundColor: '#555', margin: '8px 24px 0'}} />
                        )}
                        
                        {progressBarSettings.showTTM && (
                            <div style={{...styles.summaryItem, flex: 'none', width: '200px'}}>
                                <span style={styles.summaryLabel}>TTM (12 Months)</span>
                                <div style={{...styles.summaryValueWithProgress, flexDirection: 'row', alignItems: 'center', gap: '16px'}}>
                                    <div style={{flex: 1}}>
                                        <div style={styles.summaryProgress}>
                                            <div style={{
                                                ...styles.progressBar,
                                                width: `${ttmProgressWidth}%`,
                                                backgroundColor: getProgressBarColor(ttmProgressWidth)
                                            }} />
                                        </div>
                                    </div>
                                    <span style={{...styles.summaryValue, marginLeft: '8px'}}>{ttmHours}/{ttmTarget}h</span>
                                </div>
                            </div>
                        )}
                        
                        {((progressBarSettings.showWeekly || progressBarSettings.showMonthly || progressBarSettings.showDaily || progressBarSettings.showTTM) && progressBarSettings.showYearly) && (
                            <div style={{width: '1px', height: '48px', backgroundColor: '#555', margin: '8px 24px 0'}} />
                        )}
                        
                        {progressBarSettings.showYearly && (
                            <div style={{...styles.summaryItem, flex: 'none', width: '200px'}}>
                                <span style={styles.summaryLabel}>Year {moment().format('YYYY')}</span>
                                <div style={{...styles.summaryValueWithProgress, flexDirection: 'row', alignItems: 'center', gap: '16px'}}>
                                    <div style={{flex: 1}}>
                                        <div style={styles.summaryProgress}>
                                            <div style={{
                                                ...styles.progressBar,
                                                width: `${yearlyProgressWidth}%`,
                                                backgroundColor: getProgressBarColor(yearlyProgressWidth)
                                            }} />
                                        </div>
                                    </div>
                                    <span style={{...styles.summaryValue, marginLeft: '8px'}}>{yearlyHours}/{yearlyTarget}h</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div style={styles.timeHeaderWrapper}>
                    <div style={styles.teamColumnSpace}></div>
                    <div style={styles.timeHeader} className="timeHeader">
                        {viewMode === 'week' ? (
                            // Week view - show days
                            Array.from({ length: 7 }, (_, i) => {
                                const date = calendarDate.clone().startOf('week').add(i, 'days');
                                const isToday = date.isSame(moment(), 'day');
                                return (
                                    <div key={i} style={{
                                        ...styles.timeHeaderCell,
                                        borderRight: i === 6 ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'
                                    }}>
                                        <div style={styles.timeContentWrapper}>
                                            <span style={{
                                                ...styles.weekDayName,
                                                color: isToday ? '#EF4444' : '#C8C8C8',
                                                marginBottom: '4px',
                                                marginTop: '-12px'
                                            }} 
                                            ref={el => el && (el.style.cssText += `; color: ${isToday ? '#EF4444' : '#C8C8C8'} !important`)}
                                            >{date.format('ddd')}</span>
                                            <span style={{
                                                ...styles.weekDayDate,
                                                color: isToday ? '#EF4444' : '#C8C8C8',
                                                marginTop: '-4px'
                                            }}
                                            ref={el => el && (el.style.cssText += `; color: ${isToday ? '#EF4444' : '#C8C8C8'} !important`)}
                                            >{date.format('D')}</span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            // Day view - show hours
                            hours.map(hour => (
                                <div key={hour} style={styles.timeHeaderCell}>
                                    <div style={{
                                        ...styles.timeContentWrapper,
                                        backgroundColor: 'transparent',
                                    }}>
                                        <span 
                                            className={`time-number ${hour === currentHour ? 'current-hour' : ''}`}
                                            style={{
                                                ...styles.timeNumber,
                                                color: hour === currentHour ? '#BA2C2C' : '#C0BFBF',
                                                fontFamily: 'Inter, sans-serif',
                                                fontWeight: '800'
                                            }}
                                        >
                                            {formatHour(hour)}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Main schedule content */}
                <div style={styles.scheduleContent}>
                    {renderTeamColumn()}
                    <div style={styles.scheduleWrapper}>
                        <div style={styles.scheduleSection} className="scheduleSection">
                            {renderScheduleGrid()}
                        </div>
                    </div>
                </div>
            </div>
            <EventPopup
                isOpen={isEventPopupOpen}
                onClose={() => {
                    setIsEventPopupOpen(false);
                    setExistingEvent(null);
                    setPrefilledParticipants([]);
                    setPrefilledEmployeeId(null);
                }}
                onSave={handleEventSave}
                onDelete={handleEventDelete}
                employee={selectedEmployee}
                startTime={selectedStartTime}
                endTime={selectedEndTime}
                date={selectedDate}
                existingEvent={existingEvent}
                prefilledParticipants={prefilledParticipants}
                employees={employees}
            />
            {eventPreview && (
                <EventPreview
                    event={eventPreview.isNew ? null : eventPreview}
                    isOpen={true}
                    position={eventPreviewPosition}
                    onExpand={handleEventPreviewExpand}
                    onClose={handleEventPreviewClose}
                    onDelete={handleEventDelete}
                    employees={employees}
                    isCreating={eventPreview.isNew || false}
                    selectedDate={eventPreview.isNew ? new Date(eventPreview.start) : null}
                    selectedHour={selectedHour}
                    startTime={eventPreview.isNew ? selectedStartTime : null}
                    endTime={eventPreview.isNew ? selectedEndTime : null}
                    onCreate={handleEventSave}
                    prefilledParticipants={prefilledParticipants}
                    selectedEmployee={eventPreview.isNew ? selectedEmployee : null}
                    viewMode={viewMode}
                />
            )}
            {renderTeamMemberSelector()}
            {renderDragPreview()}
        </div>
    );
};

export default CustomCalendar; 